// ---------- rendering & viewport ----------
// Offscreen layers, to hold 60fps even at 120x120 on a phone:
//   grid  : the tracing paper grid (its alpha fades out at the end of the game)
//   base  : frozen content (placed symbols, codes of remaining cells)
//   halo  : the highlighted region, painted once then replayed at a breathing alpha
//   wave  : large zone, in a graphite version and a "fresh" version
// Per frame we blit the layers, then draw only what moves. Redrawing everything on
// every frame costs up to 12ms here (dice at 120x120) — out of budget once animating.

import { alphabet, codeRank } from './alphabet';
import { coachBoussole, coachPeutPeindre, coachReussi } from './coach';
import { NB_CODES, alphabetById, paintOrder } from '../shared/alphabets';
import {
  anim,
  animFlareEnd,
  animFlyTo,
  animPhase,
  animShakeOffset,
  animPoke,
  animResetGrid,
  animWake,
  EASE,
  FINAL_MS,
  FLARE_CROSS,
  FLARE_MS,
  FLARE_TAIL_MS,
  haloAlpha,
  noMotion,
  PLATES_FADE_MS,
  REPLAY_TRAIL_MAX,
  REPLAY_TRAIL_MS,
  RING_MS,
  TWINKLE_MS,
  VIEW_MS,
} from './anim';
import { inkBase, mixColor, setInkOverride } from '../shared/ink';
import { pal } from './palette';
import { markStyle } from './mark';
import { settings } from './settings';
import { S, ZOOM_FIT, ZOOM_MAX, ZOOM_MIN } from './state';

let gridCanvas: HTMLCanvasElement | null = null;
let baseCanvas: HTMLCanvasElement;
let haloCanvas: HTMLCanvasElement;
let waveCanvas: HTMLCanvasElement;
let waveTintCanvas: HTMLCanvasElement;
let gridReady = false,
  baseReady = false,
  haloReady = false,
  waveReady = false;
let haloCount = 0;
let wasFlying = false;
/**
 * Viewport at which each layer family was rasterized. During a gesture, everything is
 * redone every frame: clipping to visible cells makes that affordable, and a stretched
 * layer has nothing to show beyond what was on screen when it was drawn — anything
 * entering the frame would appear empty until release. One register per layer: they
 * aren't rebuilt together (filling a cell doesn't invalidate the halo, hovering doesn't
 * invalidate the base), so a shared register would get overwritten by whichever layer
 * was rebuilt most recently and would drift the stretching of layers still cached.
 */
const gridLayerViewport = { z: 1, x: 0, y: 0 };
const baseLayerViewport = { z: 1, x: 0, y: 0 };
const haloLayerViewport = { z: 1, x: 0, y: 0 };
const waveLayerViewport = { z: 1, x: 0, y: 0 };

/** Stretch of a layer from the viewport it was drawn at to the current one. A world
 *  point p is on screen at x + p*z, and on the layer at xR + p*zR: hence k = z/zR and
 *  t = x - k*xR. Does nothing if the layer is already up to date. */
function applyStretch(viewport: { z: number; x: number; y: number }): void {
  const k = S.viewport.z / viewport.z;
  const tx = S.viewport.x - k * viewport.x;
  const ty = S.viewport.y - k * viewport.y;
  if (k === 1 && tx === 0 && ty === 0) return;
  S.g.transform(k, 0, 0, k, tx * S.DPR, ty * S.DPR);
}

export function invalidateBase(): void {
  baseReady = false;
  baseShiftable = false;
  baseGen++;
}
export function invalidateHalo(): void {
  haloReady = false;
  haloCount = 0;
  zonesReady = false;
}
export function invalidateWave(): void {
  waveReady = false;
}
export function invalidateLayers(): void {
  gridReady = baseReady = haloReady = waveReady = false;
  baseShiftable = false;
  zonesReady = false;
  baseGen++;
}

/**
 * La fin d'un glissement ou d'un pincement : les couches sont à refaire au cadrage final, comme
 * avec invalidateLayers() — mais la couche des cases, si elle était à jour au début du geste,
 * pourra être DÉCALÉE au lieu d'être redessinée, quand le geste n'a pas changé le zoom (cf.
 * shiftBase).
 */
export function settleGesture(): void {
  const was = baseReady;
  invalidateLayers();
  baseShiftable = was;
}

/**
 * How much is left, by zone. Below DETAIL_MAX_CELLS neither the codes nor the marks are drawn,
 * which leaves the player with no idea where the work is. Each coarse zone shows its remaining
 * count instead — no new visual language to learn, since reading a number in a square is the
 * game itself. Zones already cleared show nothing, which is the "done here" signal.
 *
 * The count follows the lock: cells of the locked symbol if there is one, every remaining cell
 * otherwise. Two readings of the same question, "where is there still work".
 *
 * ZONES stays coarse ON PURPOSE. With no symbol locked the counts are the image's ink density,
 * so a fine grid of them would be a legible thumbnail of the drawing — the very leak the zoom
 * threshold exists to close. At ~24 zones the map carries no recognisable subject; do not
 * refine it without redoing that reasoning.
 */
const ZONES = 6; // zones along the longer side
interface Zone {
  x: number;
  y: number;
  w: number;
  h: number;
  n: number;
}
let zones: Zone[] = [];
/** Cell boundaries of the lattice, kept so the outline can cover finished zones too — they are
 *  absent from `zones`, which only holds what's left to do. */
let zoneGeo: { xs: number[]; ys: number[] } | null = null;
let zonesReady = false;
let zonesShown = false;

function buildZones(): void {
  zonesReady = true;
  zones = [];
  zoneGeo = null;
  const sym = S.lockedSymbol;
  if (!S.target.length) return;
  // Pendant le rejeu, `filled` est incomplet par construction : compter ce qu'il reste
  // afficherait des centaines de cases « à faire » sur une feuille terminée, et un tap
  // cadrerait une zone au lieu d'abréger le rejeu.
  if (anim.replay) return;
  // Même raison pendant la récolte : elle recule la caméra sur la feuille entière, donc à
  // l'endroit précis où les plaques s'allument — une volée de chiffres tomberait sur la lumière
  // qu'on est en train de regarder, et un tap cadrerait une zone au lieu de rendre la main.
  // Même chose tant que le repère est retenu (anim.haloHeld) : la fin de la pièce d'initiation
  // recule sur le dessin entier, et des chiffres dans des cadres roses s'allumaient par-dessus le
  // chien qu'on était en train de montrer (relevé en capture le 2026-10-31).
  if (anim.flare || anim.haloHeld) return;
  // Zone counts per axis, aiming at square zones so they don't stretch with the image's
  // aspect ratio. A fixed block size in cells was the first attempt and it left a remainder:
  // the last row and column came out as slivers, and the number inside them shrank with them.
  const target = Math.max(S.gridW, S.gridH) / ZONES;
  const zw = Math.max(1, Math.round(S.gridW / target));
  const zh = Math.max(1, Math.round(S.gridH / target));
  // Boundaries spread over the whole grid instead: zones then differ by at most one cell,
  // which reads as uniform.
  const xs = Array.from({ length: zw + 1 }, (_, i) => Math.round((i * S.gridW) / zw));
  const ys = Array.from({ length: zh + 1 }, (_, i) => Math.round((i * S.gridH) / zh));
  zoneGeo = { xs, ys };

  const col = new Int32Array(S.gridW);
  for (let zc = 0; zc < zw; zc++) for (let cc = xs[zc]; cc < xs[zc + 1]; cc++) col[cc] = zc;
  const row = new Int32Array(S.gridH);
  for (let zr = 0; zr < zh; zr++) for (let r = ys[zr]; r < ys[zr + 1]; r++) row[r] = zr;

  const count = new Int32Array(zw * zh);
  for (let r = 0; r < S.gridH; r++)
    for (let cc = 0; cc < S.gridW; cc++) {
      const m = S.target[r][cc];
      if (S.filled[r][cc] || m === 0) continue;
      if (sym !== null && m !== sym) continue;
      count[row[r] * zw + col[cc]]++;
    }
  for (let zr = 0; zr < zh; zr++)
    for (let zc = 0; zc < zw; zc++) {
      const n = count[zr * zw + zc];
      if (!n) continue;
      zones.push({
        x: xs[zc] * S.cellW,
        y: ys[zr] * S.cellH,
        w: (xs[zc + 1] - xs[zc]) * S.cellW,
        h: (ys[zr + 1] - ys[zr]) * S.cellH,
        n,
      });
    }
}

/** True while the zone overlay is on screen, i.e. while a tap should frame a zone rather than
 *  fill a cell — at this scale a cell is a few pixels wide and its code is hidden, so tapping
 *  to paint was aiming blind anyway. */
export function zonesOnScreen(): boolean {
  return zonesShown && !!zoneGeo && zones.length > 0;
}

/**
 * Zoom at which the fine detail comes back, i.e. at which the counts give way to the codes:
 * DETAIL_MAX_CELLS inverted rather than a factor guessed by eye. Framing a zone must land
 * BELOW that threshold, otherwise the tap answers "where is the work" with the same overview,
 * only bigger — the number the player just aimed at would still be sitting there.
 *
 * cellWindow() rounds outward and adds a cell of margin per side, so up to four cells per axis
 * come on top of the visible span; M covers that, and erring high only over-zooms slightly.
 */
function detailZoom(): number {
  const M = 4;
  const a = S.availW / S.cellW;
  const b = S.availH / S.cellH;
  // (a/z + M)(b/z + M) = DETAIL_MAX_CELLS, solved for 1/z
  const s = M * (a + b);
  const u = (Math.sqrt(s * s + 4 * a * b * (DETAIL_MAX_CELLS - M * M)) - s) / (2 * a * b);
  return u > 0 ? 1 / u : ZOOM_MAX;
}

/**
 * Cell size, in logical pixels, that the zone tap aims for. detailZoom() only guarantees the
 * codes are DRAWN: sitting exactly on the threshold puts a cell at about 10px on a phone,
 * a code you squint at and a target your finger misses. Landing there answers "where is the
 * work" but not "now go do it".
 *
 * 26px is the smaller end of comfortable — a code readable at a glance, a cell wide enough to
 * tap — while still keeping a couple of hundred cells in view, so the player sees a piece of
 * the zone and not a keyhole. Raising it further trades that context for legibility.
 */
const ZONE_CELL_PX = 26;

/** The cell left to fill closest to (r, cc) inside the zone, counted exactly as the zone's
 *  number counts — locked symbol if there is one, everything remaining otherwise. null if the
 *  zone is done. */
function pendingNear(
  r0: number,
  r1: number,
  c0: number,
  c1: number,
  r: number,
  cc: number,
): [number, number] | null {
  const sym = S.lockedSymbol;
  let best: [number, number] | null = null;
  let bd = Infinity;
  for (let y = r0; y < r1; y++)
    for (let x = c0; x < c1; x++) {
      const m = S.target[y][x];
      if (S.filled[y][x] || m === 0) continue;
      if (sym !== null && m !== sym) continue;
      const d = (y - r) * (y - r) + (x - cc) * (x - cc);
      if (d < bd) {
        bd = d;
        best = [y, x];
      }
    }
  return best;
}

/**
 * Flies the view to the zone holding cell (r, cc) and returns the zoom it is heading for —
 * the caller needs it for the slider, which would otherwise show the zoom we're leaving.
 * null when there is no overlay to act on, so the caller can fall back to painting.
 */
export function flyToZone(r: number, cc: number): number | null {
  if (!zonesOnScreen() || !zoneGeo) return null;
  const { xs, ys } = zoneGeo;
  let zc = 0;
  while (zc < xs.length - 2 && cc >= xs[zc + 1]) zc++;
  let zr = 0;
  while (zr < ys.length - 2 && r >= ys[zr + 1]) zr++;
  const c0 = xs[zc];
  const c1 = xs[zc + 1];
  const r0 = ys[zr];
  const r1 = ys[zr + 1];
  const x = c0 * S.cellW;
  const y = r0 * S.cellH;
  const w = (c1 - c0) * S.cellW;
  const h = (r1 - r0) * S.cellH;
  // A little air around the zone: framed edge to edge, the cells on the border are cut in
  // half and the neighbouring zone's context is lost.
  const PAD = 1.12;
  const fit = Math.min(S.availW / (w * PAD), S.availH / (h * PAD));
  // Three floors, the largest wins: frame the zone, cross the detail threshold so the number
  // goes away, and get the cells to a size worth playing on. On a phone the last one dominates
  // — the first two only decide small grids, where the zone already lands big.
  const read = ZONE_CELL_PX / Math.min(S.cellW, S.cellH);
  const z = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, Math.max(fit, detailZoom(), read)));
  let fx = x + w / 2;
  let fy = y + h / 2;
  // Past the framing zoom the zone no longer fits on screen, so its middle is no longer a safe
  // aim: on a zone whose work sits in one corner it would frame finished cells and answer the
  // tap with an empty page. Centring on a cell left to fill — the one nearest the finger, so
  // the view lands where the player pointed — guarantees the framing holds work.
  if (z > fit) {
    const t = pendingNear(r0, r1, c0, c1, r, cc);
    if (t) {
      fy = (t[0] + 0.5) * S.cellH;
      fx = (t[1] + 0.5) * S.cellW;
    }
  }
  animFlyTo(z, S.availW / 2 - fx * z, S.availH / 2 - fy * z);
  return z;
}

/** What the plates are left at once the sheet has been let alone. Not zero: the numbers stop
 *  covering the drawing, and the player still sees where they were and that a touch brings them
 *  back. */
const PLATES_REST = 0.12;

/**
 * Opacity of the count plates, from the date the fade started (anim.fade, 0 while they are up).
 *
 * The plates only exist zoomed out, which is exactly the moment somebody wants to look at the
 * whole picture — and they are opaque cartons laid over it. So they answer the pause: full while
 * the sheet is being worked, out of the way two and a half seconds after it is let go, and back at
 * full the instant a finger or the pointer moves again. The return is a jump and not a fade,
 * because it answers a gesture: a number arriving over half a second reads as lag.
 *
 * Under reduced motion the fade is skipped, not the behaviour — the plates still get out of the
 * way, they just do it in one step.
 */
function platesAlpha(): number {
  if (!anim.fade) return 1;
  if (noMotion()) return PLATES_REST;
  // Clamped low as well as high: the frame may be timestamped a hair before the fade started.
  const p = Math.min(1, Math.max(0, (anim.t - anim.fade) / PLATES_FADE_MS));
  return 1 - (1 - PLATES_REST) * EASE.outCubic(p);
}

/** In the accent rather than graphite, and far larger than a cell code: it has to read as a note laid
 *  over the sheet, never as one of the symbols that make up the drawing. */
function drawZones(): void {
  // No lattice either once everything is filled: the overlay exists to point at work left.
  if (!zoneGeo || !zones.length) return;
  const g = S.g;
  const { xs, ys } = zoneGeo;
  g.save();

  // Outline over EVERY zone, finished ones included: bounding only the zones that still hold
  // work would repeat what the numbers already say, and a full lattice says nothing at all.
  //
  // The lattice outlives the numbers: zoomed in they are gone, and these lines are then the
  // only thing tying the cells under the finger back to the zone the player flew into — the
  // map they were reading a moment ago. They carry that alone, so they come a shade stronger
  // than under the numbers, where the plates already anchor the reading.
  //
  // Clipped to the visible window like the grid lines: at high zoom, full-length lines are
  // mostly off-screen, and only a couple of boundaries are in view anyway.
  const v = cellWindow();
  const x0 = v.c0 * S.cellW;
  const x1 = v.c1 * S.cellW;
  const y0 = v.r0 * S.cellH;
  const y1 = v.r1 * S.cellH;
  g.strokeStyle = zonesShown ? pal().zoneLine : pal().zoneLineBare;
  // constant on screen, like the grid lines
  g.lineWidth = (zonesShown ? 1 : 2) / S.viewport.z;
  g.beginPath();
  for (let i = 1; i < xs.length - 1; i++) {
    if (xs[i] < v.c0 || xs[i] > v.c1) continue;
    const x = xs[i] * S.cellW;
    g.moveTo(x, y0);
    g.lineTo(x, y1);
  }
  for (let i = 1; i < ys.length - 1; i++) {
    if (ys[i] < v.r0 || ys[i] > v.r1) continue;
    const y = ys[i] * S.cellH;
    g.moveTo(x0, y);
    g.lineTo(x1, y);
  }
  g.stroke();

  if (!zonesShown) {
    g.restore();
    return;
  }

  // One size for every zone, from the nominal zone rather than each zone's own dimensions:
  // sizing per zone is what made the numbers uneven. Same for the plate, measured on the
  // longest count present, so a 1 and a 4-digit count sit on identical plates.
  const fs = Math.min(S.pxW / (xs.length - 1), S.pxH / (ys.length - 1)) * 0.3;
  g.font = `500 ${fs}px "IBM Plex Mono", ui-monospace, monospace`;
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  let widest = 0;
  for (const z of zones) widest = Math.max(widest, g.measureText(String(z.n)).width);
  const pw = widest + fs * 0.7;
  const ph = fs * 1.4;

  // The plates fade, the lattice does not: those lines are thin and at .3 of the accent, they hide
  // nothing, and they are what says where the numbers went.
  g.globalAlpha = platesAlpha();

  for (const z of zones) {
    const cx = z.x + z.w / 2;
    const cy = z.y + z.h / 2;
    // A plate the colour of the paper: over a zone already dotted with symbols the number was
    // unreadable, and thickening it would have made it compete with the drawing instead.
    g.fillStyle = pal().plate;
    g.beginPath();
    // roundRect is missing on older WebViews, and a throw here would kill the whole frame
    // loop — a square plate is a fine degradation.
    if (g.roundRect) g.roundRect(cx - pw / 2, cy - ph / 2, pw, ph, fs * 0.26);
    else g.rect(cx - pw / 2, cy - ph / 2, pw, ph);
    g.fill();
    g.strokeStyle = pal().plateEdge;
    g.stroke();
    // The deep accent, as everywhere the charter puts a letter on the paper: the bright one
    // is unreadable on cream, and the plate is cream.
    g.fillStyle = pal().zoneNum;
    g.fillText(String(z.n), cx, cy);
  }
  g.restore();
}

/**
 * Highlights the cells to fill (the hovered cell, or every remaining cell of the locked
 * symbol). An interface element, not a symbol: it doesn't depend on any alphabet.
 *
 * Painted as a REGION and not cell by cell. Cell by cell, a locked symbol turned the
 * sheet into a few hundred identical accent squares, each with its own border and its own
 * hatching restarted at its own corner: a mosaic of stamps laid over the drawing, which
 * is what made the whole thing read as a diagnostic overlay rather than as part of the
 * game. Once the cells are one shape:
 *   - the wash is a single gradient across the region — honey at the top, amber at the
 *     bottom — so the light varies over the sheet instead of being flat everywhere,
 *   - the hatching runs on a GLOBAL frame (lines of constant x+y, one lattice for the
 *     whole sheet) and crosses the cells rather than being cut at every edge. Same
 *     reasoning as the hachures alphabet: a per-cell trame turns an engraving into a
 *     checkerboard of vignettes,
 *   - only the SILHOUETTE is outlined — an edge is drawn where the neighbour is not
 *     highlighted — so a zone gets one contour and an isolated cell still gets its four
 *     sides, which is what detaches a 7px cell from the paper,
 *   - the silhouette is stroked twice more, wide and faint under the crisp line, which
 *     spills a soft glow onto the paper around the region. Concentric strokes rather
 *     than a real blur: `filter = 'blur()'` on a screen-sized layer is a Skia pass we
 *     cannot afford here, since a swept stroke rebuilds this layer on every frame.
 *
 * Budget (measured here, on a realistic dithered window: 50x50 visible cells at 7px, of
 * which 678 — 27% — carry the locked symbol, which is what a flat area gives):
 *   - `lineCap = 'square'` and not `'round'`: round caps on the wide strokes cost 3.9ms
 *     against 1.5, for a difference nobody can see on 1100 unit-length segments,
 *   - two glow strokes and not three,
 *   - the hatching is gated on the cell being legible on screen, which is also where its
 *     clip stops costing (the visible cell count falls as the square of the zoom).
 * Total 2.0ms against 1.2 for the per-cell version it replaces — and the per-cell one
 * paid a clip per cell, so on a solid block the region is the cheaper of the two.
 *
 * Merging collinear edges into runs was tried and dropped: on a dithered region it takes
 * 1092 segments down to 1012 (halftone edges are jagged, there are no long runs) for a
 * pass over the grid nobody gets back.
 */
/** Cell size below which the hatching is no longer legible, in SCREEN px: the marks live
 *  in world units under the viewport transform, so what matters is the cell as the eye
 *  gets it, not the cell as the grid stores it. */
const HATCH_MIN = 13;

/** Et la taille en deçà de laquelle la croix du repère « par case » ne vaut plus la peine : sous
 *  7 px de case elle fait quatre pixels de bras, et quatre pixels ne se lisent pas comme une
 *  croix mais comme du bruit. Le lavis et le liseré, eux, restent — la case ne cesse jamais
 *  d'être marquée, elle cesse seulement de porter sa croix. */
const DOT_MIN = 7;

/** Clamped ramp: a mark scales with the cell, but never thinner than the pixel that makes
 *  it visible nor thicker than what would smother a cell blown up by the zoom. */
const ramp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(v, hi));

/** Les cases marquées, aplaties en (r, c, r, c, …) : le repère « par case » a besoin de chacune
 *  et pas seulement de leur silhouette. Un tableau plat réutilisé plutôt qu'une liste de paires —
 *  il est réécrit à chaque bâti de couche, et sa taille est bornée par le même DETAIL_MAX_CELLS
 *  qui décide si le repère se dessine. Sa durée de vie utile est celle d'un appel : paintHighlight
 *  le remplit et drawMarkStatic le lit dans la même foulée, personne d'autre. */
const haloCells: number[] = [];

/**
 * Paints every cell for which `on(r, c)` holds, inside the given window. One pass over
 * the window, three paths: the cells, their silhouette, and the hatching lattice.
 */
function paintHighlight(
  ctx: CanvasRenderingContext2D,
  win: { r0: number; r1: number; c0: number; c1: number },
  on: (r: number, c: number) => boolean,
): number {
  const region = new Path2D();
  const outline = new Path2D();
  haloCells.length = 0;
  let n = 0;
  let x0 = Infinity,
    y0 = Infinity,
    x1 = -Infinity,
    y1 = -Infinity;
  for (let r = win.r0; r < win.r1; r++) {
    for (let c = win.c0; c < win.c1; c++) {
      if (!on(r, c)) continue;
      n++;
      haloCells.push(r, c);
      const x = c * S.cellW;
      const y = r * S.cellH;
      region.rect(x, y, S.cellW, S.cellH);
      if (x < x0) x0 = x;
      if (y < y0) y0 = y;
      if (x + S.cellW > x1) x1 = x + S.cellW;
      if (y + S.cellH > y1) y1 = y + S.cellH;
      // Tested against the true predicate and not against the window, so a zone running
      // off screen isn't given a contour along the edge of the screen.
      if (!on(r - 1, c)) {
        outline.moveTo(x, y);
        outline.lineTo(x + S.cellW, y);
      }
      if (!on(r + 1, c)) {
        outline.moveTo(x, y + S.cellH);
        outline.lineTo(x + S.cellW, y + S.cellH);
      }
      if (!on(r, c - 1)) {
        outline.moveTo(x, y);
        outline.lineTo(x, y + S.cellH);
      }
      if (!on(r, c + 1)) {
        outline.moveTo(x + S.cellW, y);
        outline.lineTo(x + S.cellW, y + S.cellH);
      }
    }
  }
  if (!n) return 0;

  // LA RÉSERVE, pour une feuille sans grille (bareSheet, la peinture) : les cases à remplir
  // redeviennent du papier nu sous leur marque, comme une zone qu'un peintre masque.
  //
  // Partout ailleurs une case à remplir EST un trou de papier dans le dessin, et la marque se pose
  // sur ce papier. En peinture, les touches des voisines débordent de 0,6 case sur la case vide :
  // elle est déjà à moitié peinte, et le lavis comme les hachures, translucides, se noyaient dans
  // un fond bigarré — « les cases à remplir ne se voient pas trop » une fois la feuille entamée
  // (2026-10-14), plus encore quand le rose du repère tombe sur les rouges de la toile. Le papier
  // recouvre ces débords le temps que la case reste à faire ; peinte, elle sort de la région et les
  // touches voisines reviennent, sous la sienne, dans l'ordre des taps.
  if (alphabet().bareSheet) {
    ctx.fillStyle = pal().paper;
    ctx.fill(region);
  }

  drawMarkStatic(ctx, region, outline, { x0, y0, x1, y1 });
  return n;
}

/**
 * Les hachures : le réseau GLOBAL des droites x + y = k, sur toute la feuille, dont la région ne
 * découpe qu'un morceau.
 *
 * Global, et non une trame par case : recommencer la trame au coin de chaque case referait une
 * mosaïque de vignettes posée sur le dessin, là où le joueur doit lire UNE zone. `shift` décale
 * le réseau le long de ce même axe — ce décalage, et lui seul, est ce qui les fait courir.
 *
 * UN MOTIF DE REMPLISSAGE ET NON UN DÉTOURAGE, et c'est là toute la question du coût. La première
 * version détourait la région puis retraçait les droites : correct, et gratuit tant que ça ne
 * servait qu'à cuire la couche une fois. En faire une animation, c'est le payer soixante fois par
 * seconde — mesuré à +4,4 ms par image sur la feuille de démonstration, là où tout le reste du
 * rendu en prend 1,5. Le détourage d'un chemin fait de centaines de rectangles est ce qui coûte,
 * pas les droites. Un motif rend le même dessin en UN remplissage, du prix du lavis d'à côté, et
 * l'animation devient une translation de sa matrice au lieu d'un nouveau tracé.
 *
 * La tuile est en pixels de l'ÉCRAN et le motif la ramène aux unités du monde : dessinée dans le
 * monde, elle s'étirerait avec le zoom et les rayures deviendraient des bandes.
 */
let hatchTile: HTMLCanvasElement | null = null;
let hatchKey = '';

function hatchPattern(
  ctx: CanvasRenderingContext2D,
  stepWorld: number,
  lwScreen: number,
): { pat: CanvasPattern; side: number } | null {
  const scale = S.viewport.z * S.DPR;
  // Le côté de la tuile, en pixels de l'écran. Entier : une tuile fractionnaire se rééchantillonne
  // à chaque répétition et les rayures perdent leur bord net.
  const side = Math.max(3, Math.round(stepWorld * scale));
  const lw = Math.max(1, lwScreen * S.DPR);
  const col = pal().hatch;
  const key = `${side}|${lw.toFixed(2)}|${col}`;
  if (key !== hatchKey || !hatchTile) {
    hatchTile ??= document.createElement('canvas');
    hatchTile.width = side;
    hatchTile.height = side;
    const t = hatchTile.getContext('2d');
    if (!t) return null;
    t.clearRect(0, 0, side, side);
    t.strokeStyle = col;
    t.lineWidth = lw;
    t.beginPath();
    // Trois droites et non une : celle qui traverse la tuile, plus ses deux voisines, qui ne
    // touchent que les coins. Sans elles le trait s'interrompt à chaque raccord de tuile.
    for (let n = 0; n <= 2; n++) {
      const k = n * side;
      t.moveTo(k + side, -side);
      t.lineTo(k - 2 * side, 2 * side);
    }
    t.stroke();
    hatchKey = key;
  }
  const pat = ctx.createPattern(hatchTile, 'repeat');
  return pat && { pat, side };
}

function drawHatch(ctx: CanvasRenderingContext2D, region: Path2D, shift: number): void {
  const cell = Math.min(S.cellW, S.cellH) * S.viewport.z;
  const px = 1 / S.viewport.z;
  const made = hatchPattern(ctx, ramp(cell * 0.42, 7, 34) * px, ramp(cell * 0.09, 1, 2.5));
  if (!made) return;
  // La tuile revient aux unités du monde, et le décalage s'y ajoute. Sur (1, 1) et de moitié :
  // translater de (d, d) déplace x + y de 2d, et c'est en x + y que le pas se compte.
  const k = 1 / (S.viewport.z * S.DPR);
  const p = made.side * k;
  // Ramené dans une période : une translation qui grandit sans fin finit par manger la précision
  // du flottant, et la trame se met à trembler.
  const d = (((shift / 2) % p) + p) % p;
  made.pat.setTransform(new DOMMatrix([k, 0, 0, k, d, d]));
  ctx.save();
  ctx.fillStyle = made.pat;
  ctx.fill(region);
  ctx.restore();
}

/**
 * Le repère des cases à faire — en entier, et figé.
 *
 * Il ne va plus que dans la couche en cache : rien ne s'y ajoute à l'image. Les trois styles ont
 * battu, défilé et ondulé jusqu'ici, et ce mouvement a coûté plus cher que tout le reste du jeu
 * — non pas par image, mais par DURÉE. C'était la seule animation SANS FIN : un symbole reste
 * verrouillé pendant toute une partie, donc `anim.pulse` tenait la boucle rAF éveillée et une
 * feuille posée sur la table se redessinait soixante fois par seconde pour une image identique.
 * Ce sont les joueurs qui l'ont mesuré, sur leur batterie, avant qu'on le voie dans un profil.
 *
 * Les trois choix restent — ils ne disaient pas que le mouvement, ils disent combien le repère
 * couvre le dessin — et chacun garde son dessin entier : ce que la passe animée posait sur
 * l'écran est rapatrié ici, figé à une valeur prise dans sa course. À MI-COURSE pour le halo du
 * contour, qui n'a rien à rattraper ; dans le HAUT de course pour le point, qui doit se lire sur
 * une case de 8 px sans l'aide du mouvement. La trame n'a pas de valeur à choisir : son décalage
 * était son seul paramètre animé, et zéro est un décalage comme un autre.
 *
 * Corollaire : le repère ne consulte plus noMotion(). Il n'y a plus de repli statique parce
 * qu'il n'y a plus que du statique — l'ancien mode `statique` a été réparti dans les trois, sa
 * lueur au contour et sa trame figée aux hachures.
 *
 * Ordre des passes : la lueur, le lavis, la trame, le liseré, PUIS les croix. La croix est la
 * seule à se poser SUR la case et non autour d'elle — la passe animée dessinait son ancêtre, un
 * disque, par-dessus tout — et un liseré tracé après elle la rognerait sur une petite case.
 *
 * Toutes les épaisseurs se décident en px ÉCRAN puis se reconvertissent en unités du monde,
 * sinon elles épaissiraient avec le zoom — l'ancien contour par case demandait `lineWidth = 1`
 * et obtenait une bordure de 24 px au zoom 24.
 */
function drawMarkStatic(
  ctx: CanvasRenderingContext2D,
  region: Path2D,
  outline: Path2D,
  box: { x0: number; y0: number; x1: number; y1: number },
): void {
  const px = 1 / S.viewport.z;
  const cell = Math.min(S.cellW, S.cellH) * S.viewport.z;
  const hl = pal();
  const mode = markStyle();
  ctx.save();
  ctx.lineJoin = 'miter';
  ctx.lineCap = 'square'; // carré et non rond : même rendu sur des bords unitaires, un tiers du coût

  // La lueur d'abord : le lavis recouvre ensuite sa moitié intérieure et seul le débord sur le
  // papier subsiste. Plafonnée à 22 px écran pour qu'une case grossie par le zoom garde un halo
  // et non un brouillard.
  //
  // Elle est au CONTOUR, qui ne pose rien d'autre sur les cases : c'est tout ce qui lui reste
  // pour peser, son battement parti. Les valeurs sont celles de ce battement arrêté en son
  // milieu (l'éclat allait de 0,12 à 0,38, la largeur de 0,30 à 0,56 de la case), et sous le
  // liseré et non par-dessus comme l'ancienne passe écran : le trait reste net, le halo déborde.
  if (mode === 'battant') {
    ctx.save();
    ctx.globalAlpha = 0.25;
    ctx.strokeStyle = hl.markGlow;
    ctx.lineWidth = ramp(cell * 0.43, 4, 22) * px;
    ctx.stroke(outline);
    ctx.restore();
  }

  // Le lavis. « Le contour » s'en passe, et c'est tout son propos : il ne pose rien sur les
  // cases, il n'en cerne que le bord, donc il reste lisible quand il en reste des centaines.
  if (mode !== 'battant') {
    const pale = mode === 'cases';
    const wash = ctx.createLinearGradient(0, box.y0, 0, box.y1);
    wash.addColorStop(0, pale ? hl.washPale0 : hl.wash0);
    wash.addColorStop(1, pale ? hl.washPale1 : hl.wash1);
    ctx.fillStyle = wash;
    ctx.fill(region);
  }

  // Les hachures, figées. Elles défilaient : le décalage passe à 0 et la trame reste ce
  // qu'elle est — le réseau global des droites x + y = k, qui traverse les cases au lieu d'être
  // recoupé à chacune (cf. drawHatch).
  if (mode === 'hachures' && cell >= HATCH_MIN) drawHatch(ctx, region, 0);

  // Le bord, à DEUX TONS : une bande vive, puis le trait sombre en son milieu.
  //
  // Un seul ton ne peut pas faire le travail, parce que le repère doit tenir sur deux fonds
  // opposés. Au début il se pose sur du crème nu et un liseré pâle suffit. En fin de partie il se
  // pose dans un champ de graphite — et là un ton clair seul s'évanouit, tandis que sa vraie
  // concurrence, les cases vides des AUTRES symboles, sont des trous clairs dans l'encre sombre
  // et crient plus fort qu'un écart de teinte ne le pourra jamais. Bande claire plus trait sombre
  // donne à la case un bord qui se lit contre l'encre et un qui se lit contre le papier : elle ne
  // dépend plus de ce qui l'entoure.
  //
  // « La croix par case » n'en prend qu'un, et fin : là c'est la croix qui porte le repère,
  // et un bord épais ferait deux marquages pour une seule case.
  if (mode === 'cases') {
    ctx.strokeStyle = hl.rim;
    ctx.lineWidth = ramp(cell * 0.07, 1, 2.5) * px;
    ctx.stroke(outline);
  } else {
    ctx.strokeStyle = hl.rim;
    ctx.lineWidth = ramp(cell * 0.16, 2, 5) * px;
    ctx.stroke(outline);
    ctx.strokeStyle = hl.edge;
    ctx.lineWidth = ramp(cell * 0.06, 1, 3) * px;
    ctx.stroke(outline);
  }

  // Une croix au centre de chaque case. C'est le seul repère qui montre le COMPTE — on voit
  // combien il en reste — et le seul qui ait besoin des cases une par une : d'où `haloCells`,
  // que paintHighlight vient de remplir juste avant de nous appeler. L'ordre est la seule chose
  // fragile ici, et il tient parce que ces deux-là sont dans le même fichier.
  //
  // C'ÉTAIT UN DISQUE, ET C'EST LA FORME QU'IL NE FAUT PAS. Un disque centré dans la case est
  // exactement ce que dessinent quatre des douze alphabets : les trois pointillismes posent une
  // touche ronde au centre (rayon 0,28 / 0,46 / 0,62 de la case, cf. pointilleRiche.ts) et les
  // dés posent des pastilles rondes. Sur une feuille pointilliste, un repère rond de 0,24 case a
  // donc la taille et la forme d'une PETITE TOUCHE déjà peinte : le joueur ne distingue plus la
  // case qui lui reste à faire de celle qui est faite. La teinte du repère n'y change rien, elle
  // se lit comme un pigment de plus.
  // La croix résout ça par la forme et non par la couleur : aucun code d'aucun alphabet ne trace
  // de segment centré en diagonale, donc elle ne peut ressembler à rien de peint. Elle est aussi
  // plus grande que le disque qu'elle remplace (bras de 0,3 case, soit une demi-diagonale de 0,42
  // contre un rayon de 0,24) sans toucher le bord de la case, où le liseré travaille déjà.
  //
  // DEUX TONS, pour la raison qui les impose au liseré juste au-dessus : le repère doit tenir sur
  // du crème nu au début de partie et dans un champ d'encre à la fin. Bande vive puis trait
  // sombre en son milieu, tracés sur le MÊME chemin — deux `stroke`, pas deux chemins.
  //
  // Pas d'animation ici, et il n'y en aura pas : le mouvement de ce repère est ce qui tenait la
  // boucle rAF éveillée toute une partie (cf. l'en-tête de cette fonction).
  if (mode === 'cases' && cell >= DOT_MIN) {
    ctx.save();
    const arm = Math.min(S.cellW, S.cellH) * 0.3;
    ctx.globalAlpha = 0.85;
    // Un seul chemin pour toutes les croix, et deux `stroke` : l'ancienne passe animée ouvrait
    // un chemin par case parce que chacune avait son propre rayon et son propre éclat. Elles
    // sont maintenant toutes identiques, donc 2 500 croix coûtent deux tracés. `moveTo` avant
    // chaque bras, sans quoi le chemin relierait la fin d'une croix au début de la suivante.
    ctx.beginPath();
    for (let i = 0; i < haloCells.length; i += 2) {
      const cx = (haloCells[i + 1] + 0.5) * S.cellW;
      const cy = (haloCells[i] + 0.5) * S.cellH;
      ctx.moveTo(cx - arm, cy - arm);
      ctx.lineTo(cx + arm, cy + arm);
      ctx.moveTo(cx + arm, cy - arm);
      ctx.lineTo(cx - arm, cy + arm);
    }
    ctx.strokeStyle = hl.markDot;
    ctx.lineWidth = ramp(cell * 0.16, 2, 5) * px;
    ctx.stroke();
    ctx.strokeStyle = hl.edge;
    ctx.lineWidth = ramp(cell * 0.07, 1, 2.5) * px;
    ctx.stroke();
    ctx.restore();
  }
  ctx.restore();
}

/**
 * Draws a symbol of the current alphabet in the cell (x,y,w,h).
 *
 * L'entonnoir unique de tout ce qui se peint sur la feuille, et donc le seul endroit où mettre
 * la tolérance : un code que l'alphabet courant ne connaît pas ne dessine RIEN, au lieu de le
 * laisser lever. Ce n'est pas de la prudence gratuite — un alphabet lit son code dans ses
 * propres tables (`FACES[code]` dans les dés) et une entrée absente lève un TypeError, ici, dans
 * une boucle qui tourne des milliers de fois par image : le rendu entier s'arrête. Le cas
 * n'arrive que sur une grille tirée d'un alphabet que ce build ne connaît pas, ce que le
 * catalogue écarte déjà en amont (home.ts) et le serveur avant lui (api.ts) — cette ligne est ce
 * qui fait que même une erreur dans l'un des deux ne coûte que des cases vides.
 */
export function drawGlyph(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  code: number,
): void {
  if (code < 0 || code >= NB_CODES) return;
  const a = alphabet();
  // Gratuit hors du premier appel sur une grille : l'alphabet se souvient de la dernière reçue.
  a.prepareGrid?.(S.target);
  a.prepareFilled?.(S.filled);
  a.draw(ctx, x, y, w, h, code);
}

/**
 * Snapshot of the sheet as the player left it, for the catalogue: the cells they filled, on bare
 * paper. Nothing else — an untouched puzzle returns null and keeps its question mark, so the
 * thumbnail can never reveal more than what has been earned.
 *
 * Draws the real glyphs even though a cell lands on well under a pixel. Modulating an opacity by
 * the code would have been cheaper but wrong: `quantize` is per alphabet, and in `motifs` a code
 * carries orientation as much as coverage, so code order is not a brightness order. Letting the
 * canvas average sub-pixel shapes is precisely what a render seen from far away looks like.
 */
export function renderThumb(maxPx = 180): string | null {
  if (!S.target.length) return null;
  return renderThumbOf(
    {
      gridW: S.gridW,
      gridH: S.gridH,
      target: S.target,
      alphabetId: S.alphabetId,
      filled: (r, c) => S.filled[r][c],
    },
    maxPx,
  );
}

/**
 * De quoi rasteriser une vignette SANS que la feuille soit ouverte.
 *
 * `renderThumb()` ci-dessus ne sait photographier que la feuille en cours, parce qu'elle lit
 * l'état global. Le calendrier du puzzle du jour a le besoin inverse : reconstruire la vignette
 * d'un jour peint sur un AUTRE appareil, à partir de la grille (téléchargée) et des taps
 * (synchronisés, eux) — la vignette, elle, reste locale et n'a jamais voyagé. Voir src/daily.ts.
 */
export interface ThumbSource {
  gridW: number;
  gridH: number;
  target: readonly number[][];
  alphabetId: string;
  /** Cette case est-elle peinte ? Une fonction et non un tableau : l'appelant a tantôt une
   *  matrice de booléens (la feuille en cours), tantôt un ensemble d'indices de cases (une liste
   *  de taps rejouée), et convertir l'un en l'autre pour traverser cette frontière ne servirait
   *  qu'à allouer. */
  filled: (row: number, col: number) => boolean;
}

export function renderThumbOf(src: ThumbSource, maxPx = 180): string | null {
  if (!src.target.length) return null;
  const alpha = alphabetById(src.alphabetId);
  // Le rapport de la CASE, et non le compte des cases. `asciiElargi` demande une case 0,6 fois
  // plus large que haute — c'est le pas d'une police à chasse fixe, pas un choix de mise en
  // page — donc une grille de 100 × 60 y couvre 60 × 60 de dessin. Mesurée en cases, comme ici
  // auparavant, la vignette les rendait CARRÉES : l'image sortait étirée de 1/0,6, deux tiers de
  // large en trop, sur le seul alphabet dont la case n'est pas carrée. Les deux autres rendus
  // hors feuille font déjà ce calcul — coverBlob() dans shared/convert.ts pour la couverture de
  // l'administration, run() dans src/share.ts pour l'image partagée ; la vignette du catalogue
  // était la dernière à compter en cases.
  const ar = alpha.aspect();
  const k = maxPx / Math.max(src.gridW * ar, src.gridH);
  const w = Math.max(1, Math.round(src.gridW * ar * k));
  const h = Math.max(1, Math.round(src.gridH * k));
  const cv = document.createElement('canvas');
  cv.width = w;
  cv.height = h;
  const c = cv.getContext('2d');
  if (!c) return null;
  c.fillStyle = pal().paper;
  c.fillRect(0, 0, w, h);
  const cw = w / src.gridW;
  const ch = h / src.gridH;
  // La grille de CETTE vignette, qui n'est pas forcément la feuille ouverte ; drawGlyph rendra la
  // sienne à l'alphabet au prochain glyphe de la feuille.
  alpha.prepareGrid?.(src.target);
  // Et ses cases PEINTES, pour un alphabet qui se dessine selon ses voisines (le point de croix) :
  // sans ça, prepareGrid() le laisse croire la grille entière brodée, et une feuille en cours
  // sortait avec un creux sombre au bord de chaque case peinte, là où le jeu montre le papier
  // (revue du 2026-10-30 ; même défaut que celui que stitchedSoFar.ts corrige pour le partage).
  // Toutes les cases d'ici sont posées d'une passe : l'état final suffit.
  if (alpha.prepareFilled)
    alpha.prepareFilled(src.target.map((row, r) => row.map((_, cc) => src.filled(r, cc))));
  let any = false;
  for (const [r, cc] of paintOrder(src.target, alpha)) {
    if (!src.filled(r, cc)) continue;
    any = true;
    alpha.draw(c, cc * cw, r * ch, cw, ch, src.target[r][cc]);
  }
  if (!any) return null;
  return cv.toDataURL('image/png');
}

/** First code IN PALETTE ORDER the sheet still has a cell for, or null on a finished (or empty)
 *  sheet. Going through the alphabet's order rather than taking the lowest number is the whole
 *  point: a sheet opens on the first chip whatever that chip is, and in the shapes alphabet the
 *  lowest number is the full cell — the one symbol we no longer want a sheet to open on. */
function firstCodeTodo(): number | null {
  const rank = codeRank();
  let best: number | null = null;
  let bestRank = NB_CODES;
  for (let r = 0; r < S.gridH; r++)
    for (let c = 0; c < S.gridW; c++) {
      const m = S.target[r][c];
      if (m > 0 && !S.filled[r][c] && rank[m] < bestRank) {
        bestRank = rank[m];
        best = m;
      }
    }
  return best;
}

export function resetPlay(): void {
  S.filled = S.target.map((row) => row.map((l) => l === 0)); // empty cells count as done
  // A sheet opens on its first symbol rather than on no selection at all. Unlocked, a tap fills
  // whatever it lands on and the zone counts tally every cell left — which is the whole sheet,
  // the one number that says nothing about what to do next. Locked, they count that symbol, the
  // halo lights up where it goes, and the palette shows which chip is live. It is also the state
  // every player ends up in by their second tap; starting there just skips the empty one.
  S.lockedSymbol = firstCodeTodo();
  // Deliberately does NOT persist: this runs on every mode switch and on loading a puzzle, and
  // a save here would wipe the record we are about to restore. Persisting is the caller's move.
  S.taps.length = 0;
  S.score = 0;
  S.combo = 0;
  S.comboAt = 0;
  S.gameOver = false;
  S.justFinished = false;
  S.rating = 0;
  animResetGrid();
  invalidateLayers();
  render();
  updateProgress();
}

/** Margin around the grid, in world units (so it zooms too). */
const MARGIN = 13;

/**
 * Reveals taps[from..to): the replay's one mutation and its one draw, kept together so they
 * cannot drift apart.
 *
 * Drawn straight onto the frozen layer rather than by invalidating it. A rebuild walks every
 * filled cell, which is up to 12ms on a large sheet — affordable when something changed, not
 * sixty times a second for six seconds. Adding this frame's few dozen glyphs to a layer that
 * already holds the rest is the same picture for a thousandth of the cost.
 *
 * At the layer's OWN viewport, not the live one: the two agree here (the replay is watched
 * from a still camera) but a layer is always drawn in the frame it was rasterised in, and
 * borrowing the current viewport would offset the new cells the moment anything moved.
 *
 * When the layer isn't ready — a camera flight is on, something invalidated it — there is
 * nothing to add to: `filled` is the source of truth and the rebuild reads it back.
 */
export function revealTaps(from: number, to: number): void {
  const c = baseReady ? baseCanvas.getContext('2d') : null;
  if (c) {
    c.save();
    c.setTransform(S.DPR, 0, 0, S.DPR, 0, 0);
    c.translate(baseLayerViewport.x, baseLayerViewport.y);
    c.scale(baseLayerViewport.z, baseLayerViewport.z);
  }
  for (let i = from; i < to; i++) {
    const idx = S.taps[i];
    const r = Math.floor(idx / S.gridW);
    const cc = idx % S.gridW;
    // A tap from an older build can point outside the grid — skipped, exactly as restore()
    // skips it, rather than taking the replay down.
    if (r >= S.gridH || cc >= S.gridW) continue;
    S.filled[r][cc] = true;
    if (c) drawGlyph(c, cc * S.cellW, r * S.cellH, S.cellW, S.cellH, S.target[r][cc]);
  }
  if (c) c.restore();
}

/**
 * The trail: the cells that have just landed, redrawn over the frozen layer in fresh ink and
 * cooling to graphite with age. It is the whole colour of the replay — the sheet fills in from
 * a warm front that leaves the finished drawing behind it.
 *
 * The same "ink arrives hot" the bloom-in uses on a single fill, and skipped for the same
 * reason on an alphabet that carries its own colours: tinting the pigments of the pointillist
 * sheet would wash the drawing out, and there is nothing to tint them towards.
 *
 * Newest batch first, on a budget: past REPLAY_TRAIL_MAX cells the oldest are dropped mid-batch
 * — they are the coolest, i.e. the ones already indistinguishable from the layer beneath.
 */
function drawReplayTrail(): void {
  const rp = anim.replay;
  if (!rp || !rp.batches.length || alphabet().selfColored) return;
  const g = S.g;
  const fresh = pal().fresh;
  const cold = inkBase();
  let budget = REPLAY_TRAIL_MAX;
  for (let b = rp.batches.length - 1; b >= 0 && budget > 0; b--) {
    const bt = rp.batches[b];
    const age = Math.min(1, (anim.t - bt.t) / REPLAY_TRAIL_MS);
    const from = Math.max(bt.from, bt.to - budget);
    budget -= bt.to - from;
    setInkOverride(mixColor(fresh, cold, EASE.outCubic(age)));
    for (let i = from; i < bt.to; i++) {
      const idx = S.taps[i];
      const r = Math.floor(idx / S.gridW);
      const cc = idx % S.gridW;
      if (r >= S.gridH || cc >= S.gridW) continue;
      drawGlyph(g, cc * S.cellW, r * S.cellH, S.cellW, S.cellH, S.target[r][cc]);
    }
  }
  setInkOverride(null);
}

/**
 * Frames the whole sheet, over time. The replay is watched from here and nowhere else: at
 * playing zoom a couple of hundred cells are on screen, and a replay of ten thousand would
 * spend its six seconds off-frame. Returns the flight's duration so the caller can hold the
 * first cell back until the camera has settled.
 */
/**
 * Centre une case, au zoom courant. C'est ce que fait un tap sur la boussole : elle montre où
 * le travail continue, et le zoom appartient au joueur — le déplacer, oui ; redécider à quelle
 * échelle il peint, non. Même règle que handBackView() après un palier.
 */
export function flyToCell(r: number, c: number): void {
  const z = S.viewport.z;
  animFlyTo(z, S.availW / 2 - (c + 0.5) * S.cellW * z, S.availH / 2 - (r + 0.5) * S.cellH * z);
}

/** Le cadrage de la feuille entière, sans y aller : flyToWhole() s'y rend au pas ordinaire, le
 *  prototype de la fenêtre (src/essai.ts) au pas d'un recul de cinéma. */
export function wholeView(): { z: number; x: number; y: number } {
  // L'AJUSTEMENT et non le plancher : depuis que le joueur peut reculer plus loin (ZOOM_MIN),
  // « montrer la feuille entière » n'est plus « reculer au maximum ». La récolte et le rejeu
  // cadrent la feuille, ils ne la miniaturisent pas.
  const z = ZOOM_FIT;
  const x = (S.availW - (S.pxW + 2 * MARGIN) * z) / 2 + MARGIN * z;
  const y = (S.availH - (S.pxH + 2 * MARGIN) * z) / 2 + MARGIN * z;
  return { z, x, y };
}

export function flyToWhole(): number {
  const { z, x, y } = wholeView();
  if (S.viewport.z === z && S.viewport.x === x && S.viewport.y === y) return 0;
  animFlyTo(z, x, y);
  // Instant under reduced motion, and then there is nothing to wait for.
  return noMotion() ? 0 : VIEW_MS;
}

/**
 * Recenters the viewport. The grid, margin included, is the object being framed: as
 * long as it fits in the box it's centered; once it overflows we only prevent an edge
 * from entering the screen, so there's never empty space left on one side.
 */
function clampView(): void {
  const z = S.viewport.z;
  const pw = (S.pxW + 2 * MARGIN) * z;
  const ph = (S.pxH + 2 * MARGIN) * z;
  const m = MARGIN * z;
  if (pw <= S.availW) S.viewport.x = (S.availW - pw) / 2 + m;
  else S.viewport.x = Math.max(S.availW - pw + m, Math.min(m, S.viewport.x));
  if (ph <= S.availH) S.viewport.y = (S.availH - ph) / 2 + m;
  else S.viewport.y = Math.max(S.availH - ph + m, Math.min(m, S.viewport.y));
}

/**
 * The sheet itself: MARGIN of paper around the grid, rounded corners, and a shadow that lifts
 * it off the stage.
 *
 * It used to be .sheetbox's CSS background, i.e. the paper filled the box whatever the drawing
 * did with it. But the box is the VIEWPORT, not the page: a square grid in a portrait box left
 * some 440pt of cream past the picture, top and bottom, which reads as a page half filled
 * rather than as a sheet laid down. Painted here it hugs the grid at zoom 1 and covers the
 * whole box once zoomed in — with no layout that has to know the puzzle's aspect ratio.
 *
 * In world units, under the world transform, so it zooms, bounces and shakes with the grid.
 * Everything is divided by the zoom to stay the same few pixels on screen instead of turning
 * into a fog at zoom 12 (where the edge is off-screen anyway).
 *
 * The shadow is three strokes rather than a shadowBlur: the blur measured 2.6ms of an 11.3ms
 * frame, 30% of the budget for a decoration, and paid again on every frame of every gesture. A
 * stroke only touches its own perimeter, where the blur reworks the whole rectangle.
 */
function paintPaper(g: CanvasRenderingContext2D): void {
  const z = S.viewport.z;
  const w = S.pxW + 2 * MARGIN;
  const h = S.pxH + 2 * MARGIN;
  const r = Math.min(9, MARGIN);
  g.save();
  // Outward, fading: widest and faintest first, so the darkest ring ends up against the edge.
  for (const [spread, alpha] of [
    [6, 0.05],
    [3.5, 0.11],
    [1.5, 0.2],
  ] as const) {
    const s = spread / z;
    g.strokeStyle = `rgba(0, 0, 0, ${alpha})`;
    g.lineWidth = 2 / z;
    g.beginPath();
    // Rayon plancher à 0 : roundRect LÈVE sur un rayon négatif, et une exception ici emporte
    // l'image entière — le canevas est déjà effacé quand on en arrive là. La cause d'origine
    // était un zoom négatif, corrigée dans anim.ts ; ce plancher est là pour qu'aucune autre ne
    // puisse jamais coûter la feuille.
    g.roundRect(-MARGIN - s, -MARGIN - s + 2 / z, w + 2 * s, h + 2 * s, Math.max(0, r + s));
    g.stroke();
  }
  g.fillStyle = pal().paper;
  g.beginPath();
  g.roundRect(-MARGIN, -MARGIN, w, h, r);
  g.fill();
  g.restore();
}

/** Largeur du passe-partout, en pixels d'ÉCRAN : converti en unités monde à l'usage, comme les
 *  filets de paintPaper. C'est ce qui le garde d'épaisseur constante quel que soit le zoom du
 *  joueur — un passe-partout dont la marge grandit avec le zoom n'est plus une marge, c'est un
 *  fond. */
const MAT_PX = 22;

/**
 * Le passe-partout, et son filet : ce qui fait d'une feuille terminée un tirage encadré.
 *
 * Il POUSSE VERS L'EXTÉRIEUR et ne rétrécit pas la feuille, et c'est un choix de prudence autant
 * que de dessin. Rétrécir voulait dire toucher à `ech`, qui passe dans place() et dans la
 * transformation monde : le dessin aurait été rendu à une échelle que le viewport ne connaît pas,
 * donc décalé de tout ce qui teste un point — un pincement après la fin aurait glissé sous le
 * doigt. La marge extérieure ne coûte rien de tout ça, et la lecture est la même : ce qui encadre
 * une image, c'est ce qu'il y a autour d'elle.
 *
 * Tenu et non joué : `mat` vaut 1 tant que la feuille reste terminée, pas seulement pendant la
 * séquence. Un cadre qui se retirerait au bout de 2,4 s laisserait le joueur devant une feuille
 * nue avec une carte de félicitations dessus.
 */
function paintMat(g: CanvasRenderingContext2D, mat: number): void {
  if (mat <= 0) return;
  const z = S.viewport.z;
  // La marge ajoutée, en unités monde : une largeur d'écran divisée par le zoom.
  const grow = (MAT_PX * mat) / z;
  const w = S.pxW + 2 * MARGIN + 2 * grow;
  const h = S.pxH + 2 * MARGIN + 2 * grow;
  const x = -MARGIN - grow;
  const y = -MARGIN - grow;
  g.save();
  // L'ombre du montage, portée par le passe-partout et non par la feuille : c'est lui qui est
  // désormais l'objet posé sur le fond.
  g.shadowColor = `rgba(0, 0, 0, ${0.5 * mat})`;
  g.shadowBlur = 26 / z;
  g.shadowOffsetY = 8 / z;
  g.fillStyle = pal().paper;
  g.beginPath();
  g.roundRect(x, y, w, h, Math.max(0, 3 / z));
  g.fill();
  g.shadowColor = 'transparent';
  // Le filet, tracé : la ligne se referme autour du montage au lieu d'apparaître d'un coup.
  const per = 2 * (w + h);
  g.strokeStyle = pal().matEdge;
  g.lineWidth = 1.4 / z;
  g.setLineDash([per * mat, per]);
  g.beginPath();
  g.roundRect(x + grow * 0.45, y + grow * 0.45, w - grow * 0.9, h - grow * 0.9, Math.max(0, 2 / z));
  g.stroke();
  g.setLineDash([]);
  g.restore();
}

// ---------- layers ----------
function layerContext(
  cv: HTMLCanvasElement,
  viewport: { z: number; x: number; y: number },
): CanvasRenderingContext2D {
  const c = cv.getContext('2d')!;
  c.setTransform(S.DPR, 0, 0, S.DPR, 0, 0);
  // the layers are screen-sized, not grid-sized: that's what bounds memory usage while
  // the sheet itself can overflow
  c.clearRect(0, 0, S.availW, S.availH);
  c.translate(S.viewport.x, S.viewport.y);
  c.scale(S.viewport.z, S.viewport.z);
  viewport.z = S.viewport.z;
  viewport.x = S.viewport.x;
  viewport.y = S.viewport.y;
  return c;
}

function sizeLayers(): void {
  if (!gridCanvas) {
    gridCanvas = document.createElement('canvas');
    baseCanvas = document.createElement('canvas');
    haloCanvas = document.createElement('canvas');
    waveCanvas = document.createElement('canvas');
    waveTintCanvas = document.createElement('canvas');
  }
  if (!anim.mask || anim.mask.length !== S.gridW * S.gridH) animResetGrid();
  const devW = Math.round(S.availW * S.DPR);
  const devH = Math.round(S.availH * S.DPR);
  for (const cv of [gridCanvas, baseCanvas, haloCanvas, waveCanvas, waveTintCanvas]) {
    if (cv.width !== devW || cv.height !== devH) {
      cv.width = devW;
      cv.height = devH;
      invalidateLayers();
    }
  }
}

function buildGrid(): void {
  drawGridOn(layerContext(gridCanvas!, gridLayerViewport));
  gridReady = true;
}

function drawGridOn(c: CanvasRenderingContext2D): void {
  if (alphabet().bareSheet && !alphabet().ruledSheet) return;
  // Clipped: at high zoom, drawing 242 lines eight times longer than the screen wastes
  // time. A single path, a single stroke.
  const v = cellWindow();
  const x0 = v.c0 * S.cellW;
  const x1 = v.c1 * S.cellW;
  const y0 = v.r0 * S.cellH;
  const y1 = v.r1 * S.cellH;
  c.strokeStyle = pal().ruling;
  c.lineWidth = 1 / S.viewport.z;
  // Half-screen-pixel offset for a crisp line (otherwise it spreads over two pixels,
  // blurry). Expressed in world units: without dividing by zoom, this half pixel would
  // grow with S.viewport.z and the line would visibly drift from the cells once you zoom.
  const halfPx = 0.5 / S.viewport.z;
  c.beginPath();
  for (let i = v.c0; i <= v.c1; i++) {
    c.moveTo(i * S.cellW + halfPx, y0);
    c.lineTo(i * S.cellW + halfPx, y1);
  }
  for (let i = v.r0; i <= v.r1; i++) {
    c.moveTo(x0, i * S.cellH + halfPx);
    c.lineTo(x1, i * S.cellH + halfPx);
  }
  c.stroke();
}

/** Visible window, in cell indices (with one cell of margin): beyond zoom 2, almost
 *  the whole grid is off-screen, and walking it is wasted time. */
function cellWindow() {
  const z = S.viewport.z;
  const c0 = Math.max(0, Math.floor(-S.viewport.x / z / S.cellW) - 1);
  const c1 = Math.min(S.gridW, Math.ceil((S.availW - S.viewport.x) / z / S.cellW) + 1);
  const r0 = Math.max(0, Math.floor(-S.viewport.y / z / S.cellH) - 1);
  const r1 = Math.min(S.gridH, Math.ceil((S.availH - S.viewport.y) / z / S.cellH) + 1);
  return { r0, r1, c0, c1 };
}

function buildBase(): void {
  const c = layerContext(baseCanvas, baseLayerViewport);
  baseGen++;
  baseFromSheet = drawSheetAtRest(c);
  baseCodes = baseFromSheet ? false : drawBaseOn(c);
  baseReady = true;
  if (baseFromSheet) startSharpen();
}

/** La couche des cases est, pour l'instant, la recopie de l'image de la feuille (drawSheetAtRest) :
 *  son dessin exact est en cours (sharpenSlice). Elle ne se décale pas (cf. shiftBase) — la refaire ne
 *  coûte qu'une recopie, et ses bandes neuves se dessineraient à côté de pixels qui viennent de
 *  l'image. */
let baseFromSheet = false;
/** Change à chaque fois que la couche des cases est refaite ou déclarée périmée : un affinage
 *  commencé sous un autre numéro ne la remplace pas. */
let baseGen = 0;

/**
 * Le lâcher d'un pincement ou d'un vol en vue d'ensemble : l'image de la feuille D'ABORD, le
 * dessin exact ENSUITE.
 *
 * LE PROBLÈME (mesuré le 2026-10-21 pour la fresque, cf. FRESQUE.md et PERF.md). L'image de la
 * feuille rend le GESTE fluide, mais elle était interdite à l'arrêt : au lâcher, la couche se
 * refaisait en redessinant chaque case peinte de l'écran, d'un bloc. En vue d'ensemble c'est
 * toute la feuille — sur une feuille peinte à 60 %, processeur ×4, une image figée de 150 ms à
 * 120 cases, 422 à 250, 1,5 s à 500. shiftBase n'y peut rien : il ne sert qu'au glissement, et
 * en vue d'ensemble on ne glisse pas, on pince.
 *
 * POURQUOI PAS L'IMAGE TOUT COURT. Elle a été essayée à l'arrêt (2026-10-22) et ne s'y confond
 * PAS avec le dessin exact : réduite de son niveau à l'écran, elle lisse ce que le dessin case par
 * case laisse voir. En briques sur 250 cases, à 3,5 px par case, le fin quadrillage des joints
 * disparaît des fonds sombres — 9/255 d'écart moyen, 13 % des pixels à plus de 24. Pendant un
 * geste ça passe (on l'y accepte depuis le 2026-10-14) ; sur une feuille qu'on regarde, non.
 *
 * DONC LES DEUX, l'un après l'autre. L'image est posée au lâcher — une recopie, l'aspect que la
 * feuille avait déjà sous les doigts —, puis le dessin exact se refait par tranches, pendant
 * les temps morts (sharpenSlice), et la remplace d'un coup quand il est complet. La feuille redevient
 * nette une fraction de seconde après le lâcher, comme une carte qu'on vient de zoomer.
 *
 * LES CONDITIONS, chacune avec sa raison :
 *  - toutes celles de drawSheetImage (alphabet qui a une image, grille assez grande, aucune
 *    éclosion ni vague ni rejeu : leurs cases sont masquées de la couche et dessinées en direct) ;
 *  - aucun numéro dans la couche : l'image n'en porte pas. À cette taille il n'y en a de toute
 *    façon jamais — ils n'apparaissent que sous DETAIL_MAX_CELLS cases à l'écran ;
 *  - un niveau au moins aussi fin que l'écran : l'image n'est jamais AGRANDIE à l'arrêt, même le
 *    temps de l'affinage — l'agrandissement n'est admis que pendant un geste (SHEET_STRETCH_MAX) ;
 *  - une image DÉJÀ construite pour cette feuille. La construire coûte plus que la couche
 *    qu'elle remplace : ce chemin n'en déclenche jamais la construction complète (refreshSheet
 *    sans `allowFull`), il ne fait que la mettre à jour des cases changées, et s'abstient sinon.
 *
 * ON NE PEINT JAMAIS SUR UNE COUCHE AINSI FAITE. Un tap ne remplit qu'en dessous de
 * DETAIL_MAX_CELLS cases à l'écran (zonesOnScreen) : une case y fait une vingtaine de pixels
 * physiques sur un téléphone, et aucun niveau de l'image n'est aussi fin. bakeCells n'a donc pas à
 * savoir d'où vient la couche sur laquelle il pose.
 */
function drawSheetAtRest(c: CanvasRenderingContext2D): boolean {
  if (!alphabet().sheetMode) return false;
  if (S.gridW * S.gridH <= DETAIL_MAX_CELLS) return false;
  if (anim.replay || anim.wave || anim.pend.length) return false;
  if (codesVisible()) return false;
  if (!sheetLevels.length || sheetKey !== sheetKeyNow() || sheetTarget !== S.target) return false;
  const px = S.cellH * S.viewport.z * S.DPR;
  // Le plus grossier qui reste au moins aussi fin que l'écran : réduit d'un rapport de 1 à 2,
  // jamais agrandi.
  let l: SheetLevel | null = null;
  for (const lv of sheetLevels) if (lv.r >= px) l = lv;
  if (!l) return false;
  if (!refreshSheet(false)) return false;
  c.save();
  c.imageSmoothingEnabled = true;
  // « low », comme pendant le geste (cf. drawSheetImage) : l'image ne reste à l'écran que le temps
  // de l'affinage, et le lâcher montre ainsi la feuille telle qu'elle était sous les doigts.
  // « high » a été essayé : il coûtait plus que le dessin case par case qu'il remplaçait sur les
  // alphabets en damier, qui posent DEUX images (motifsTrait sur 120 cases, processeur ×4 : lâcher
  // de 89 ms avant, 136 avec « high »).
  c.imageSmoothingQuality = 'low';
  for (const cv of l.cvs) c.drawImage(cv, -S.cellW, -S.cellH, cv.width / l.s, cv.height / l.s);
  c.restore();
  return true;
}

// ---------- l'affinage : le dessin exact, par tranches, pendant les temps morts ----------
/** La couche exacte en cours de dessin, à la taille des couches. */
let sharpCanvas: HTMLCanvasElement | null = null;
let sharpJob: {
  gen: number;
  /** la fenêtre de cases de la reconstruction, prise au départ (cellWindow) */
  win: { r0: number; r1: number; c0: number; c1: number };
  /** où l'on en est : la prochaine ligne de cases, ou le prochain tap pour un alphabet joué dans
   *  l'ordre */
  next: number;
  /** nombre de taps au départ : une case peinte entre-temps manquerait à la couche */
  taps: number;
} | null = null;
let sharpIdle = 0;

/**
 * Le temps de dessin d'une tranche, au plus, en ms. Un temps mort peut durer 50 ms, et un doigt
 * qui se pose pendant une tranche attend qu'elle finisse : on la garde courte, sous une image.
 */
const SHARPEN_SLICE_MS = 8;
/** Taps dessinés entre deux regards à l'horloge, pour un alphabet joué dans l'ordre. */
const SHARPEN_TAPS = 128;

function startSharpen(): void {
  const devW = baseCanvas.width;
  const devH = baseCanvas.height;
  if (!sharpCanvas) sharpCanvas = document.createElement('canvas');
  if (sharpCanvas.width !== devW || sharpCanvas.height !== devH) {
    sharpCanvas.width = devW;
    sharpCanvas.height = devH;
  }
  // Le contexte d'une reconstruction (layerContext), posé une fois : les tranches le reprennent
  // tel que la précédente l'a laissé, exactement comme les cases successives d'un même appel.
  const c = sharpCanvas.getContext('2d')!;
  c.setTransform(S.DPR, 0, 0, S.DPR, 0, 0);
  c.clearRect(0, 0, S.availW, S.availH);
  c.translate(baseLayerViewport.x, baseLayerViewport.y);
  c.scale(baseLayerViewport.z, baseLayerViewport.z);
  const win = cellWindow();
  sharpJob = {
    gen: baseGen,
    win,
    next: alphabet().orderedPlay ? 0 : win.r0,
    taps: S.taps.length,
  };
  scheduleSharpen();
}

function scheduleSharpen(): void {
  if (sharpIdle) return;
  sharpIdle =
    typeof requestIdleCallback === 'function'
      ? requestIdleCallback(sharpenSlice, { timeout: 300 })
      : (setTimeout(sharpenSlice, 16) as unknown as number);
}

/**
 * Une tranche d'affinage : quelques lignes de cases (ou quelques taps), puis la main rendue. La
 * dernière remplace la couche.
 *
 * AU PIXEL PRÈS IDENTIQUE à une reconstruction d'un bloc, et c'est ce qui fixe le découpage : la
 * reconstruction est ÉTALÉE, pas découpée. Les mêmes appels de dessin, dans le même ordre, sur le
 * même contexte — les lignes de la fenêtre l'une après l'autre (drawBaseOn sur une ligne à la
 * fois), ou les taps dans leur ordre pour un alphabet joué dans l'ordre (drawTapsRange). Un
 * premier essai découpait l'écran en bandes de pixels, chacune sous sa découpe, comme shiftBase :
 * le canvas n'anticrénèle pas tout à fait de même une forme coupée par une découpe et une forme
 * entière, et les coutures se voyaient à la comparaison — jusqu'à 80/255 sur deux lignes de
 * pixels de part et d'autre de chaque bande, en pointillisme (2026-10-22).
 *
 * Abandonnée, sans rien remplacer, dès que la couche a été refaite ou déclarée périmée (baseGen),
 * ou qu'une case a été peinte entre-temps. Suspendue pendant un geste, un vol ou une animation de
 * cases : la branche directe ne lit pas cette couche, et le lâcher la refera de toute façon.
 */
function sharpenSlice(): void {
  sharpIdle = 0;
  const job = sharpJob;
  if (!job || !sharpCanvas) return;
  if (job.gen !== baseGen || !baseFromSheet || S.taps.length !== job.taps) {
    sharpJob = null;
    return;
  }
  if (S.gesture || anim.view !== null || anim.replay || anim.wave || anim.pend.length) {
    scheduleSharpen();
    return;
  }
  const c = sharpCanvas.getContext('2d')!;
  const w = job.win;
  const ordered = alphabet().orderedPlay;
  const end = ordered ? job.taps : w.r1;
  const t0 = performance.now();
  while (job.next < end && performance.now() - t0 < SHARPEN_SLICE_MS) {
    if (ordered) {
      const to = Math.min(end, job.next + SHARPEN_TAPS);
      drawTapsRange(c, w, job.next, to);
      job.next = to;
    } else {
      drawBaseOn(c, { r0: job.next, r1: job.next + 1, c0: w.c0, c1: w.c1 });
      job.next++;
    }
  }
  if (job.next < end) {
    scheduleSharpen();
    return;
  }
  // Complète : elle remplace la recopie de l'image, d'un seul coup.
  const b = baseCanvas.getContext('2d')!;
  b.save();
  b.setTransform(1, 0, 0, 1, 0, 0);
  b.clearRect(0, 0, baseCanvas.width, baseCanvas.height);
  b.drawImage(sharpCanvas, 0, 0);
  b.restore();
  baseFromSheet = false;
  sharpJob = null;
  renderSoon();
}

/** La couche des cases était à jour au début du geste qui vient de finir (cf. settleGesture). */
let baseShiftable = false;
/** La copie de travail du décalage, à la taille des couches. */
let shiftCanvas: HTMLCanvasElement | null = null;

/**
 * Après un glissement, la couche des cases est DÉCALÉE et seules les bandes entrées dans l'écran
 * sont dessinées, au lieu de tout redessiner. Vrai si c'est fait, faux s'il faut reconstruire.
 *
 * LE PROBLÈME (2026-10-20). Pendant un geste l'image de la feuille sert (drawSheetImage) ; au
 * lâcher, invalidateLayers() refaisait la couche au cadrage final, c'est-à-dire chaque case peinte
 * de l'écran, en une image. En peinture, 2 500 touches : la saccade au moment où le doigt quitte la
 * vitre, sur toutes les techniques, plus forte là où le dessin d'une case coûte le plus. Or un
 * glissement ne change que la translation : tout ce qui était à l'écran y est encore, au même
 * zoom, seulement ailleurs.
 *
 * AU PIXEL PRÈS, et c'est ce qui impose l'arrondi. Décaler une image d'une fraction de pixel la
 * rééchantillonne — elle s'adoucit, et le décalage suivant l'adoucit encore. Le cadrage final est
 * donc ARRONDI pour que le décalage tombe sur un nombre entier de pixels physiques : un
 * déplacement d'au plus un demi-pixel physique, invisible au lâcher d'un glissement.
 *
 * PAS AU PIXEL PRÈS POUR AUTANT, et aucun décalage ne pourrait l'être : le canvas ne rend pas le
 * même tracé à l'identique d'une position à l'autre. Mesuré le 2026-10-20 dans Chrome, hors du
 * jeu : les mêmes touches de peinture dessinées 260 pixels plus loin diffèrent sur 2 % des
 * valeurs, sur les bords anticrénelés. La couche décalée, comparée à une reconstruction au même
 * cadrage, en diffère d'autant : 1,4 % en peinture nuancée, 0,03 % en briques — le bruit de
 * rendu, pas une erreur de dessin. Ce qui rend ce bruit inoffensif : le décalage étant entier, il
 * ne s'accumule pas d'un glissement à l'autre.
 *
 * Le gain, même banc (Pixel 7a émulé, processeur ×4, 113 × 150 cases à moitié peintes, zoom de
 * jeu, douze glissements) : la pire image du lâcher passe de 97 à 51 ms en peinture nuancée, de
 * 62 à 47 en briques ; rien en motifs zoomé de près (27 contre 29), où il y a peu de cases à
 * l'écran. Le reste du lâcher, c'est la surbrillance et les deux recopies plein écran.
 *
 * Les bandes neuves sont redessinées une à une, chacune sous sa découpe et sans recouvrement — un
 * pixel dessiné deux fois doublerait l'opacité des bords anticrénelés — avec les cases dont le
 * dessin peut les toucher (une case de marge, comme cellWindow, pour les touches qui débordent),
 * dans l'ordre même de la reconstruction : lignes, ou taps pour un alphabet joué dans l'ordre.
 *
 * Refusé, et la couche reconstruite comme avant : un pincement (le zoom a changé) ; un décalage
 * plus grand que l'écran ; un arrondi que clampView déferait ; ou des numéros qui apparaîtraient ou
 * disparaîtraient avec le nouveau cadrage (codesVisible compte les cases de l'écran entier).
 * Seule la couche des cases y a droit : la grille ne coûte qu'un tracé, et la surbrillance pose
 * des hachures calées sur l'écran que le décalage ferait glisser.
 */
function shiftBase(): boolean {
  const lv = baseLayerViewport;
  const v = S.viewport;
  if (lv.z !== v.z) return false;
  // Une couche recopiée de l'image se refait d'une recopie : la décaler coûterait autant, et ses
  // bandes neuves se dessineraient case par case, à côté de pixels qui viennent de l'image.
  if (baseFromSheet) return false;
  const devW = baseCanvas.width;
  const devH = baseCanvas.height;
  const sx = Math.round((v.x - lv.x) * S.DPR);
  const sy = Math.round((v.y - lv.y) * S.DPR);
  if (Math.abs(sx) >= devW || Math.abs(sy) >= devH) return false;
  const was = { x: v.x, y: v.y };
  v.x = lv.x + sx / S.DPR;
  v.y = lv.y + sy / S.DPR;
  const snapped = { x: v.x, y: v.y };
  clampView();
  if (v.x !== snapped.x || v.y !== snapped.y || codesVisible() !== baseCodes) {
    v.x = was.x;
    v.y = was.y;
    return false;
  }
  if (sx || sy) {
    if (!shiftCanvas) shiftCanvas = document.createElement('canvas');
    if (shiftCanvas.width !== devW || shiftCanvas.height !== devH) {
      shiftCanvas.width = devW;
      shiftCanvas.height = devH;
    }
    const t = shiftCanvas.getContext('2d')!;
    t.clearRect(0, 0, devW, devH);
    t.drawImage(baseCanvas, 0, 0);
    const c = baseCanvas.getContext('2d')!;
    c.setTransform(1, 0, 0, 1, 0, 0);
    c.clearRect(0, 0, devW, devH);
    c.drawImage(shiftCanvas, sx, sy);
    // Les bandes découvertes, en pixels physiques : la verticale sur toute la hauteur, puis
    // l'horizontale SANS le coin que la verticale a déjà pris.
    const bands: Array<[number, number, number, number]> = [];
    const bx0 = sx > 0 ? 0 : devW + sx;
    if (sx) bands.push([bx0, 0, Math.abs(sx), devH]);
    const hx0 = sx > 0 ? sx : 0;
    if (sy) bands.push([hx0, sy > 0 ? 0 : devH + sy, devW - Math.abs(sx), Math.abs(sy)]);
    for (const [x, y, w, h] of bands) {
      c.save();
      c.setTransform(1, 0, 0, 1, 0, 0);
      c.beginPath();
      c.rect(x, y, w, h);
      c.clip();
      c.setTransform(S.DPR, 0, 0, S.DPR, 0, 0);
      c.translate(v.x, v.y);
      c.scale(v.z, v.z);
      // la fenêtre des cases sous la bande, une case de marge pour les débords
      const wx = (px: number) => (px / S.DPR - v.x) / v.z;
      const wy = (py: number) => (py / S.DPR - v.y) / v.z;
      drawBaseOn(c, {
        c0: Math.max(0, Math.floor(wx(x) / S.cellW) - 1),
        c1: Math.min(S.gridW, Math.ceil(wx(x + w) / S.cellW) + 1),
        r0: Math.max(0, Math.floor(wy(y) / S.cellH) - 1),
        r1: Math.min(S.gridH, Math.ceil(wy(y + h) / S.cellH) + 1),
      });
      c.restore();
    }
  }
  lv.x = v.x;
  lv.y = v.y;
  baseReady = true;
  return true;
}

/** La couche figée porte-t-elle les numéros des cases à remplir ? Retenu à chaque construction :
 *  c'est ce qui décide si un remplissage peut se passer de la reconstruire (cf. baseNeedsRebuild). */
let baseCodes = false;

/**
 * Faut-il reconstruire la couche figée au moment d'un remplissage ?
 *
 * Seulement si elle montre les numéros. Les cases qu'on vient de remplir sont alors masquées
 * pendant leur éclosion (anim.mask) et leur numéro doit disparaître tout de suite. Sans numéros —
 * dès que plus de DETAIL_MAX_CELLS cases sont à l'écran —, ces cases sont VIDES dans la couche
 * avant comme après : la reconstruire redessinerait chaque case peinte de l'écran pour obtenir
 * exactement les mêmes pixels. Mesuré le 2026-10-14 (processeur ×4, zone de 3 744 cases sur une
 * feuille de 128×150 à moitié peinte) : c'était une image de 255 ms en briques, au moment même du
 * tap.
 */
export function baseNeedsRebuild(): boolean {
  return !baseReady || baseCodes;
}

/**
 * Les cases dont l'éclosion se termine rejoignent la couche figée — POSÉES DESSUS au lieu de la
 * reconstruire, quand c'est possible sans que rien ne se voie.
 *
 * Reconstruire redessine toutes les cases peintes de l'écran ; mesuré sur la même zone de 3 744
 * cases, c'était une image de 517 ms en briques à la fin de l'animation. Poser seulement les
 * nouvelles ne coûte que leur dessin — et pour une vague (un grand remplissage), même pas : sa
 * couche à elle (buildWave) contient déjà leurs dessins, au même cadrage, et une seule recopie
 * suffit. Recopier un calque transparent revient exactement à y avoir dessiné (l'opération « par
 * dessus » est associative).
 *
 * Les conditions, chacune avec sa raison :
 *  - la couche est prête et prise au cadrage courant, sinon il n'y a rien sur quoi poser ;
 *  - elle ne montre pas de numéros, que les nouvelles cases devraient d'abord effacer ;
 *  - l'alphabet dessine DANS sa case (Alphabet.bakesOnTop). Une reconstruction dessine les cases
 *    dans l'ordre des lignes ; posées après coup, les nouvelles passent au-dessus de leurs
 *    voisines. Pour un dessin qui déborde (une touche de pointillisme, rayon 0,62), l'ordre se
 *    verrait là où deux dessins se recouvrent ; pour une brique, qui reste dans sa case, il ne
 *    change rien de visible. C'est mesuré alphabet par alphabet, et déclaré par chacun.
 *    Un alphabet joué dans l'ordre (orderedPlay, la peinture) a son propre chemin, cf.
 *    bakeInTapOrder : sa couche se reconstruit dans l'ordre des taps, pas des lignes.
 * Sinon, la couche est reconstruite comme avant.
 */
export function bakeCells(
  cells: ReadonlyArray<[number, number]>,
  waveZone: ReadonlyArray<[number, number]> | null,
): void {
  const v = S.viewport;
  const same =
    baseReady &&
    baseLayerViewport.z === v.z &&
    baseLayerViewport.x === v.x &&
    baseLayerViewport.y === v.y;
  if (same && !baseCodes && alphabet().orderedPlay && bakeInTapOrder(cells, waveZone)) return;
  if (!same || baseCodes || !alphabet().bakesOnTop) {
    invalidateBase();
    return;
  }
  const c = baseCanvas.getContext('2d')!;
  const waveSame =
    !!waveZone &&
    waveReady &&
    (!waveOrder || waveOrder.zone === waveZone) &&
    waveLayerViewport.z === v.z &&
    waveLayerViewport.x === v.x &&
    waveLayerViewport.y === v.y;
  // La vague d'un seul geste, quand sa couche est au même cadrage ; sinon ses cases se dessinent
  // avec les autres.
  const todo = [...cells];
  if (waveSame) {
    // La vague s'arrête sans garantie qu'une image l'ait montrée à son rayon final : ses
    // dernières cases sont dessinées ici si le front ne les a pas encore atteintes.
    growWave(Infinity);
    c.save();
    c.setTransform(1, 0, 0, 1, 0, 0);
    c.drawImage(waveCanvas, 0, 0);
    c.restore();
  } else if (waveZone) todo.push(...waveZone);
  // Un alphabet qui se dessine selon ses voisines peintes (Alphabet.prepareFilled) : celles des
  // cases posées ont changé de voisinage, elles se redessinent aussi — par-dessus, puisque le
  // dessin reste dans la case. La vague comprise : ses cases du bord touchent des cases peintes
  // avant elle, que la couche de la vague ne contient pas.
  if (alphabet().prepareFilled) {
    const seen = new Set(todo.map(([r, cc]) => r * S.gridW + cc));
    for (const [r, cc] of [...todo, ...(waveSame ? waveZone! : [])])
      for (const [rr, c2] of [
        [r - 1, cc],
        [r + 1, cc],
        [r, cc - 1],
        [r, cc + 1],
      ]) {
        if (rr < 0 || rr >= S.gridH || c2 < 0 || c2 >= S.gridW) continue;
        const i = rr * S.gridW + c2;
        if (seen.has(i) || !S.filled[rr][c2]) continue;
        seen.add(i);
        todo.push([rr, c2]);
      }
  }
  const w = cellWindow();
  c.save();
  c.setTransform(S.DPR, 0, 0, S.DPR, 0, 0);
  c.translate(v.x, v.y);
  c.scale(v.z, v.z);
  for (const [r, cc] of todo.sort((a, b) => a[0] - b[0] || a[1] - b[1])) {
    if (r < w.r0 || r >= w.r1 || cc < w.c0 || cc >= w.c1) continue;
    const lvl = S.target[r][cc];
    if (S.filled[r][cc] && lvl > 0) drawGlyph(c, cc * S.cellW, r * S.cellH, S.cellW, S.cellH, lvl);
  }
  c.restore();
}

/**
 * bakeCells() pour un alphabet joué dans l'ordre : les cases qui finissent d'éclore sont posées sur
 * la couche DANS L'ORDRE DES TAPS. Vrai si c'est fait, faux si la couche doit être reconstruite.
 *
 * POURQUOI LA PEINTURE Y A DROIT SANS ÊTRE bakesOnTop. Sa touche déborde de sa case et l'ordre de
 * pose décide qui recouvre qui — mais sa couche se reconstruit dans l'ordre des taps (drawTapsOn),
 * et les cases qui viennent d'éclore sont, par construction, les DERNIERS taps. Les poser
 * par-dessus la couche existante revient exactement à la reconstruire : mêmes dessins, même ordre,
 * même composition « par-dessus ». Sans ce chemin, chaque tap se terminait par une reconstruction
 * de toutes les touches de l'écran — de l'ordre de 50 ms sur un Pixel 7a pour 2 500 touches
 * (cf. gradientOf dans shared/alphabets/peinture.ts), plus sur un milieu de gamme : la saccade
 * ressentie sur une heure de peinture nuancée (2026-10-20).
 *
 * LA GARDE : les cases à poser doivent être exactement la queue de S.taps, et plus aucune case ne
 * doit être en train d'éclore ailleurs. Sinon une case tapée AVANT elles manquerait encore à la
 * couche et viendrait plus tard par-dessus, à l'envers de l'ordre — le cas d'une éclosion encore en
 * cours quand le réglage « sans animation » pose une zone d'un coup. On reconstruit alors, comme
 * avant.
 *
 * La couche de la vague n'est PAS recopiée, contrairement au chemin bakesOnTop : elle contient la
 * zone seule, et les petites éclosions tapées avant la vague doivent passer dessous. Les cases sont
 * donc dessinées une à une — le coût d'une zone, jamais celui de l'écran.
 */
function bakeInTapOrder(
  cells: ReadonlyArray<[number, number]>,
  waveZone: ReadonlyArray<[number, number]> | null,
): boolean {
  if (anim.mask?.includes(1)) return false;
  const fresh = new Set<number>();
  for (const [r, cc] of cells) fresh.add(r * S.gridW + cc);
  if (waveZone) for (const [r, cc] of waveZone) fresh.add(r * S.gridW + cc);
  const from = S.taps.length - fresh.size;
  if (from < 0) return false;
  for (let i = from; i < S.taps.length; i++) if (!fresh.has(S.taps[i])) return false;
  const w = cellWindow();
  const v = S.viewport;
  const c = baseCanvas.getContext('2d')!;
  c.save();
  c.setTransform(S.DPR, 0, 0, S.DPR, 0, 0);
  c.translate(v.x, v.y);
  c.scale(v.z, v.z);
  for (let i = from; i < S.taps.length; i++) {
    const idx = S.taps[i];
    const r = Math.floor(idx / S.gridW);
    const cc = idx % S.gridW;
    if (r < w.r0 || r >= w.r1 || cc < w.c0 || cc >= w.c1) continue;
    const lvl = S.target[r][cc];
    if (S.filled[r][cc] && lvl > 0) drawGlyph(c, cc * S.cellW, r * S.cellH, S.cellW, S.cellH, lvl);
  }
  c.restore();
  return true;
}

// ---------- l'image de la feuille, pour les gestes dézoomés ----------
//
// LE PROBLÈME. Pendant un geste ou un vol de caméra, la branche directe de render() redessine
// CHAQUE case peinte visible à chaque image. Dézoomé sur une grande feuille, c'est 18 000 cases
// par image (120×150) ; mesuré le 2026-10-14 dans Chrome, processeur ralenti ×4 pour approcher un
// téléphone milieu de gamme : 94 ms par image en pointillisme riche, 212 en dés, 313 en briques.
// Ni regrouper les tracés par couleur (154 ms, pire) ni dessiner à demi-résolution (74 / 191 ms)
// n'y changeaient grand-chose : ce qui coûte, c'est le NOMBRE d'appels de dessin, pas les pixels.
//
// LA RÉPONSE. Une image de toute la feuille, gardée en mémoire à plusieurs résolutions (8, 4,
// 2… pixels par case, chacune moitié de la précédente) et posée d'un seul drawImage pendant le
// mouvement. Un appel par image au lieu de 18 000.
//
// SANS CHANGEMENT VISIBLE, et c'est la contrainte qui fixe toutes les conditions de
// drawSheetImage() :
//   - RÉDUITE D'ABORD : on prend le niveau le plus fin dont la résolution atteint celle de
//     l'écran, donc un rapport entre 1 et 2. Au-delà du niveau le plus fin, l'image était
//     JAMAIS AGRANDIE (floue) et le dessin direct reprenait ; elle l'est désormais jusqu'à
//     SHEET_STRETCH_MAX, cf. sa note (2026-10-18) ;
//   - SEULEMENT au-delà de DETAIL_MAX_CELLS cases visibles : en deçà, la branche directe dessine
//     aussi les numéros des cases à remplir, que l'image ne contient pas ;
//   - JAMAIS pendant le rejeu ni une vague : leurs cases sont dessinées en direct, l'image les
//     montrerait deux fois. Pendant une PETITE éclosion, si (2026-10-23) : l'image tient les cases
//     masquées (anim.mask) pour vides, comme la couche figée, et drawBlooms les pose par-dessus
//     (cf. inSheet) ;
//   - JAMAIS à l'arrêt : la couche en cache et son dessin exact restent ce qu'on regarde ;
//   - SELON L'ALPHABET (Alphabet.sheetMode, et sa note dans shared/alphabets/types.ts) : une
//     image pour ceux dont les cases ne se touchent pas (pointillismes, briques), DEUX en damier
//     pour ceux dont le dessin touche le bord de la case (motifs, dés), aucune pour les hachures
//     et l'ASCII. Le damier existe pour les joints : à l'arrêt, deux cases voisines anticrénèlent
//     chacune leur bord commun et la grille transparaît entre elles ; une image unique, dessinée
//     sur des bords entiers, les effaçait pendant le geste (11 % des pixels). Posées en deux
//     images, deux voisines composent leurs bords séparément, comme deux remplissages : les
//     joints reviennent. Remplir les deux moitiés du damier en deux grands chemins, sans image,
//     les reproduisait aussi (0,7/255) mais coûtait 2 à 4 fois PLUS que case par case.
// Comparé dans le jeu, image en mouvement contre image à l'arrêt au même cadrage, Vermeer à
// 128×150 : pointillisme riche 1,7/255 d'écart moyen, briques 4,3/255, motifs 1,2/255, dés
// 3,7/255, et 3 % de pixels au plus à plus de 24/255 — rien de visible à la loupe ×3.
//
// À JOUR SANS TOUT REFAIRE. L'image retient l'état de remplissage qu'elle montre (sheetSeen) et
// le compare à S.filled avant de servir ; seules les cases qui ont changé sont redessinées, avec
// leurs voisines — une touche de pointillisme déborde de sa case (rayon 0,62), donc la zone est
// effacée sur une case autour puis repeinte dans l'ordre des lignes, l'ordre du dessin direct, ce
// qui garde les chevauchements identiques. Tout le reste (alphabet, grille, taille de case,
// encre, papier) force une reconstruction complète, faite à la première image d'un geste — le
// coût d'UNE image du dessin direct d'aujourd'hui — ou, le plus souvent, avant, au repos
// (scheduleSheet).

interface SheetLevel {
  /** une image, ou deux pour le damier : celle des cases (r + c) paires, puis des impaires */
  cvs: HTMLCanvasElement[];
  /** pixels de l'image par unité monde */
  s: number;
  /** pixels de l'image par hauteur de case */
  r: number;
}
let sheetLevels: SheetLevel[] = [];
let sheetKey = '';
let sheetTarget: number[][] | null = null;
let sheetSeen: Uint8Array | null = null;
let sheetIdle = 0;

/** Pixels du niveau le plus fin, toutes images comprises : 3 M, soit 12 Mo, et un tiers de plus
 *  pour les niveaux inférieurs — 16 Mo au plus. Une grille de 128×150 tient à 12 px par case en
 *  une image (15 Mo avec ses niveaux 6 et 3), à 8 en damier ; une de 250×250, à 6 et 4. Cela couvre la feuille entière et un peu de
 *  zoom, là où sont les 19 000 cases qui coûtaient ; plus près, le dessin direct reprend.
 *
 *  Le budget était de 5 M (27 Mo avec les niveaux inférieurs) et a été RÉDUIT à la relecture :
 *  le jeu tient déjà cinq couches à la taille de l'écran, quelque 25 Mo sur un téléphone, et
 *  l'image se prépare au repos même pour qui ne fait jamais de geste. Doubler la mémoire
 *  graphique d'une partie, c'est risquer, sur un téléphone d'entrée de gamme, une WebView qui
 *  recharge ou un canvas qui reste vide sans erreur — une régression pire que la saccade qu'on
 *  enlève. Pas en dessous de 3 M : à 2,5 M, le damier d'une grille de 128×150 (2 × 19 760 cases
 *  à 64 px, 2,53 M) retombait à 4 px par case, sous la résolution de l'écran, et les dés
 *  perdaient tout le gain sans que rien ne le signale. */
const SHEET_BUDGET = 3_000_000;
/** Le côté maximal d'un canvas que tous les WebView acceptent sans le refuser en silence. */
const SHEET_MAX_SIDE = 4096;

function sheetKeyNow(): string {
  return `${alphabet().id}|${S.gridW}|${S.gridH}|${S.cellW}|${S.cellH}|${inkBase()}|${pal().paper}`;
}

/**
 * Une case que l'image de la feuille montre : peinte, et pas en train d'éclore. Celles qui éclosent
 * sont absentes de la couche figée (anim.mask) et dessinées en direct par drawBlooms ; l'image les
 * ignore de même, et c'est ce qui lui permet de servir PENDANT une éclosion.
 *
 * POURQUOI (2026-10-23). La récolte recule sur la feuille entière dès que la dernière case d'un fil
 * est posée — pendant que celle-ci éclôt encore (POP_MS). L'image refusait alors de servir, et
 * chaque image du recul redessinait toutes les cases visibles : en point de croix, le renard en
 * 82 × 110, processeur ×4, le vol de 420 ms ne montrait que deux ou trois images (167 puis 250 ms),
 * et la peinture nuancée quatre images de 110 ms. Image permise : 33 ms par image après la première.
 * La case qui éclôt change l'image deux fois, quand elle se masque puis quand elle rejoint la
 * couche — deux petits rectangles à repeindre (cf. refreshSheet).
 */
function inSheet(r: number, cc: number): boolean {
  return S.filled[r][cc] && !anim.mask?.[r * S.gridW + cc];
}

/** Les glyphes des cases peintes dont le dessin peut toucher la zone [r0, r1) × [c0, c1) : une case
 *  de plus de chaque côté, pour les débords. Dans l'ordre des lignes, comme drawBaseOn.
 *  `parity` : seulement les cases (r + c) de cette parité, pour l'une des deux images du damier. */
function paintSheetCells(
  c: CanvasRenderingContext2D,
  r0: number,
  r1: number,
  c0: number,
  c1: number,
  parity: number | null,
): void {
  // Un alphabet joué dans l'ordre se dessine dans l'ordre des taps, comme sa couche figée (cf.
  // drawTapsOn) : l'image compose alors le même tableau, recouvrements compris.
  if (alphabet().orderedPlay) {
    for (const idx of S.taps) {
      const r = Math.floor(idx / S.gridW);
      const cc = idx % S.gridW;
      if (r < r0 - 1 || r > r1 || cc < c0 - 1 || cc > c1) continue;
      if (parity !== null && ((r + cc) & 1) !== parity) continue;
      const lvl = S.target[r][cc];
      if (inSheet(r, cc) && lvl > 0) drawGlyph(c, cc * S.cellW, r * S.cellH, S.cellW, S.cellH, lvl);
    }
    return;
  }
  for (let r = Math.max(0, r0 - 1); r < Math.min(S.gridH, r1 + 1); r++) {
    for (let cc = Math.max(0, c0 - 1); cc < Math.min(S.gridW, c1 + 1); cc++) {
      if (parity !== null && ((r + cc) & 1) !== parity) continue;
      const lvl = S.target[r][cc];
      if (inSheet(r, cc) && lvl > 0) drawGlyph(c, cc * S.cellW, r * S.cellH, S.cellW, S.cellH, lvl);
    }
  }
}

/** Contexte d'une image, en unités monde, l'origine décalée d'une case pour la marge de débord. */
function sheetContext(cv: HTMLCanvasElement, l: SheetLevel): CanvasRenderingContext2D {
  const c = cv.getContext('2d')!;
  c.setTransform(l.s, 0, 0, l.s, S.cellW * l.s, S.cellH * l.s);
  return c;
}

/** La parité que porte l'image `k` d'un niveau : null pour une image unique. */
function parityOf(l: SheetLevel, k: number): number | null {
  return l.cvs.length > 1 ? k : null;
}

function buildSheet(): void {
  sheetLevels = [];
  const mode = alphabet().sheetMode;
  if (!mode) return;
  const images = mode === 'checker' ? 2 : 1;
  // La plus fine résolution qui tienne dans le budget et dans les côtés, en puissance de deux :
  // chaque niveau est alors exactement la moitié du précédent, et une zone de cases tombe sur
  // des pixels entiers à tous les niveaux.
  //
  // DOUZE ET SIX AUSSI, pas seulement des puissances de deux (2026-10-14). Avec 16, 8, 4, 2, une
  // grille de 103×120 ne tenait pas 16 dans le budget et retombait à 8 px par case — à peine plus
  // que les 7,5 qu'une case occupe à l'écran sur un Pixel 7a. Le moindre pincement passait au-
  // dessus et l'image cessait de servir : en briques, un pincement de ×1,3 restait au dessin
  // direct, 376 ms par image au processeur ×4, quand une 128×150 (qui, elle, tenait ses 8 px à
  // l'aise) tournait à 49. La chaîne 12, 6, 3 garde des pixels entiers à chaque niveau, dans le
  // même budget, et couvre cette grille jusqu'à ×1,6.
  const wCells = (S.gridW + 2) * (S.cellW / S.cellH);
  const hCells = S.gridH + 2;
  const fits = (r: number) =>
    wCells * r <= SHEET_MAX_SIDE &&
    hCells * r <= SHEET_MAX_SIDE &&
    images * wCells * hCells * r * r <= SHEET_BUDGET;
  let r = [16, 12, 8, 6, 4, 3, 2].find(fits) ?? 0;
  if (!r) return;
  for (; r >= 1 && Number.isInteger(r); r /= 2) {
    const s = r / S.cellH;
    const cvs: HTMLCanvasElement[] = [];
    for (let k = 0; k < images; k++) {
      const cv = document.createElement('canvas');
      cv.width = Math.ceil((S.gridW + 2) * S.cellW * s);
      cv.height = Math.ceil(hCells * r);
      cvs.push(cv);
    }
    sheetLevels.push({ cvs, s, r });
  }
  const top = sheetLevels[0];
  top.cvs.forEach((cv, k) =>
    paintSheetCells(sheetContext(cv, top), 0, S.gridH, 0, S.gridW, parityOf(top, k)),
  );
  for (let i = 1; i < sheetLevels.length; i++) {
    sheetLevels[i].cvs.forEach((cv, k) => {
      const c = cv.getContext('2d')!;
      c.imageSmoothingQuality = 'high';
      c.drawImage(sheetLevels[i - 1].cvs[k], 0, 0, cv.width, cv.height);
    });
  }
}

/** Repeint la zone [r0, r1) × [c0, c1) de tous les niveaux : le plus fin par ses glyphes, les
 *  autres en le réduisant. */
function repaintSheetRegion(r0: number, r1: number, c0: number, c1: number): void {
  const top = sheetLevels[0];
  const x = c0 * S.cellW;
  const y = r0 * S.cellH;
  const w = (c1 - c0) * S.cellW;
  const h = (r1 - r0) * S.cellH;
  top.cvs.forEach((cv, k) => {
    const c = sheetContext(cv, top);
    c.save();
    c.beginPath();
    c.rect(x, y, w, h);
    c.clip();
    c.clearRect(x, y, w, h);
    paintSheetCells(c, r0, r1, c0, c1, parityOf(top, k));
    c.restore();
  });
  for (let i = 1; i < sheetLevels.length; i++) {
    const hi = sheetLevels[i - 1];
    const lo = sheetLevels[i];
    // En pixels du niveau fin, décalés de la marge d'une case ; le niveau grossier est la moitié.
    // On lit deux pixels de plus autour de la zone et on coupe au bord : le filtre de réduction
    // regarde les voisins, et lire seulement la zone donnerait une couture à son bord.
    const sx = (x + S.cellW) * hi.s;
    const sy = (y + S.cellH) * hi.s;
    const sw = w * hi.s;
    const sh = h * hi.s;
    const pad = 2;
    lo.cvs.forEach((cv, k) => {
      const lc = cv.getContext('2d')!;
      lc.imageSmoothingQuality = 'high';
      lc.save();
      lc.beginPath();
      lc.rect(sx / 2, sy / 2, sw / 2, sh / 2);
      lc.clip();
      lc.clearRect(sx / 2, sy / 2, sw / 2, sh / 2);
      lc.drawImage(
        hi.cvs[k],
        sx - pad,
        sy - pad,
        sw + 2 * pad,
        sh + 2 * pad,
        (sx - pad) / 2,
        (sy - pad) / 2,
        (sw + 2 * pad) / 2,
        (sh + 2 * pad) / 2,
      );
      lc.restore();
    });
  }
}

/**
 * Met l'image à jour avec S.filled. Faux si elle n'est pas utilisable : grille trop grande pour le
 * budget, pas encore de feuille — ou, sans `allowFull`, une reconstruction complète serait
 * nécessaire et on la remet à plus tard.
 *
 * DEUX COÛTS, ET UN SEUL A LE DROIT DE PASSER AU REPOS (2026-10-14). La mise à jour tournait dans
 * un rappel de temps libre (scheduleSheet) après chaque remplissage, d'un seul bloc. Mesuré au
 * profileur, processeur ×4 : une grande zone en briques déclenchait une reconstruction complète
 * (339 ms), une petite zone de motifs une mise à jour case par case (1 064 ms pour 161 cases —
 * chaque case recopiait une région à chaque niveau de chaque image). Un « temps libre » qui dure
 * une seconde fige le jeu au tap suivant : c'était la lenteur ressentie en remplissant.
 *  - Les cases changées sont maintenant repeintes en UN rectangle, celui qui les englobe : une
 *    passe par niveau au lieu d'une par case.
 *  - Une reconstruction complète ne se fait plus qu'à la demande d'un geste (`allowFull`), au
 *    premier geste qui en a besoin — une image au prix du dessin direct d'avant, une seule fois.
 *    Sauf la PREMIÈRE construction d'une feuille, qui se fait encore au repos : sans elle, le
 *    premier pincement après l'ouverture payait toute l'image d'un coup (une image de plus de
 *    400 ms en peinture, processeur ×4). Au repos, elle tombe quand le joueur ne touche à rien.
 */
function refreshSheet(allowFull: boolean): boolean {
  if (!S.filled?.length || !S.target?.length) return false;
  const key = sheetKeyNow();
  const n = S.gridW * S.gridH;
  const rebuild = () => {
    buildSheet();
    sheetKey = key;
    sheetTarget = S.target;
    sheetSeen = new Uint8Array(n);
    for (let r = 0; r < S.gridH; r++)
      for (let cc = 0; cc < S.gridW; cc++) sheetSeen[r * S.gridW + cc] = inSheet(r, cc) ? 1 : 0;
    return sheetLevels.length > 0;
  };
  if (key !== sheetKey || sheetTarget !== S.target || !sheetSeen || sheetSeen.length !== n) {
    // Une feuille sans image du tout : la construire au repos (cf. l'en-tête).
    return allowFull || key !== sheetKey || sheetTarget !== S.target ? rebuild() : false;
  }
  if (!sheetLevels.length) return false;
  // Le rectangle des cases changées, sans rien écrire encore : si c'est trop grand et qu'une
  // reconstruction n'est pas permise, l'image reste telle quelle et sheetSeen aussi — la
  // différence sera retrouvée au prochain passage.
  let r0 = S.gridH;
  let r1 = -1;
  let c0 = S.gridW;
  let c1 = -1;
  for (let r = 0; r < S.gridH; r++) {
    for (let cc = 0; cc < S.gridW; cc++) {
      if (sheetSeen[r * S.gridW + cc] !== (inSheet(r, cc) ? 1 : 0)) {
        if (r < r0) r0 = r;
        if (r > r1) r1 = r;
        if (cc < c0) c0 = cc;
        if (cc > c1) c1 = cc;
      }
    }
  }
  if (r1 < 0) return true;
  // Un rectangle qui couvre plus du quart de la feuille coûte autant qu'une reconstruction.
  if ((r1 - r0 + 1) * (c1 - c0 + 1) > n / 4) return allowFull ? rebuild() : false;
  repaintSheetRegion(
    Math.max(0, r0 - 1),
    Math.min(S.gridH, r1 + 2),
    Math.max(0, c0 - 1),
    Math.min(S.gridW, c1 + 2),
  );
  for (let r = r0; r <= r1; r++)
    for (let cc = c0; cc <= c1; cc++) sheetSeen[r * S.gridW + cc] = inSheet(r, cc) ? 1 : 0;
  return true;
}

/** Le niveau à poser pour une case de `px` pixels physiques à l'écran : le plus grossier qui reste
 *  au moins aussi fin que l'écran. À défaut — l'écran est plus fin que le niveau le plus fin —,
 *  ce niveau-là AGRANDI, jusqu'à SHEET_STRETCH_MAX ; null au-delà. */
function sheetLevelFor(px: number): SheetLevel | null {
  let best: SheetLevel | null = null;
  for (const l of sheetLevels) if (l.r >= px) best = l;
  // Réduite plus de moitié, l'image perdrait des détails que le dessin direct garde. La chaîne
  // 12, 6, 3 s'arrête à 3 px par case (cf. buildSheet) : en dessous, c'est le dessin direct.
  if (best && best.r > 2 * px) return null;
  if (!best && sheetLevels.length && sheetLevels[0].r * SHEET_STRETCH_MAX >= px)
    return sheetLevels[0];
  return best;
}

/**
 * Jusqu'où l'image la plus fine s'AGRANDIT pendant un geste ou un vol, au lieu de céder la place
 * au dessin direct. L'en-tête disait « jamais agrandie », pour ne rien montrer de flou ; c'est
 * revenu sur la table le 2026-10-18, mesuré sur un Pixel 7a sur une création de peinture nuancée
 * terminée : dès qu'on quittait la vue d'ensemble, l'image (12 à 16 px par case) n'était plus assez
 * fine et chaque touche visible se repeignait à chaque image — 100 à 150 ms par image, 217 au
 * pire, même avec le dégradé en cache (cf. gradientOf dans shared/alphabets/peinture.ts). Agrandie,
 * l'image est un peu douce tant que les doigts bougent et redevient nette au lâcher, quand les
 * couches se reconstruisent : le compromis d'une carte ou d'une galerie de photos.
 *
 * ×3 POUR VOIR, pas encore une valeur arrêtée : au-delà, une case fait 36 px physiques et il en
 * reste moins d'un millier à l'écran, ce que le dessin direct tient.
 */
const SHEET_STRETCH_MAX = 3;

/**
 * Pose l'image de la feuille à la place des glyphes, si toutes les conditions de l'en-tête sont
 * réunies ; faux sinon, et l'appelant dessine en direct comme avant. `g` est déjà sous la
 * transformation monde. `ech` est le rebond de fin, qui agrandit l'écran.
 */
function drawSheetImage(g: CanvasRenderingContext2D, ech: number): boolean {
  if (!alphabet().sheetMode) return false;
  if (S.gridW * S.gridH <= DETAIL_MAX_CELLS) return false;
  // Pas pendant une vague ni le rejeu ; pendant une petite éclosion, si (cf. inSheet).
  if (anim.replay || anim.wave) return false;
  const v = cellWindow();
  // Le seuil des numéros : en deçà, la branche directe les écrit, et l'image ne les a pas. Sans
  // objet là où il n'y en a aucun — un alphabet sans numéros (bareSheet), une feuille finie — et
  // c'est précisément là que l'image agrandie (cf. SHEET_STRETCH_MAX) sert le plus.
  const numbered = !alphabet().bareSheet && !S.gameOver;
  if (numbered && (v.r1 - v.r0) * (v.c1 - v.c0) <= DETAIL_MAX_CELLS) return false;
  if (!refreshSheet(true)) return false;
  const l = sheetLevelFor(S.cellH * S.viewport.z * ech * S.DPR);
  if (!l) return false;
  g.save();
  g.imageSmoothingEnabled = true;
  // LISSAGE « low », le filtre bilinéaire, et pas « high ». Le rapport de réduction reste sous
  // 2 (cf. sheetLevelFor), là où un bilinéaire lit déjà tous les pixels sources utiles : comparé
  // dans le jeu le 2026-10-14 contre l'image à l'arrêt, l'écart est le même au centième près
  // (pointillisme riche 1,74 contre 1,73/255, briques 4,24 contre 4,30), et l'image en mouvement
  // coûte moitié moins — 22 ms au lieu de 44, processeur ×4, la pose de l'image étant alors tout
  // ce qui reste : le quadrillage, soupçonné, ne pesait que 5 ms.
  g.imageSmoothingQuality = 'low';
  // Une image, ou les deux du damier l'une après l'autre : chacune compose ses bords de case
  // séparément, comme deux remplissages, et c'est ce qui rend les joints.
  for (const cv of l.cvs) g.drawImage(cv, -S.cellW, -S.cellH, cv.width / l.s, cv.height / l.s);
  g.restore();
  return true;
}

/**
 * Prépare l'image au repos, pour que le premier geste ne paie pas sa construction. Seulement sur
 * une grille où elle servira un jour (plus de DETAIL_MAX_CELLS cases), et au plus une fois en
 * attente : chaque remplissage la redemande, la mise à jour ne coûte alors que les cases changées.
 */
function scheduleSheet(): void {
  if (sheetIdle || !alphabet().sheetMode || S.gridW * S.gridH <= DETAIL_MAX_CELLS) return;
  const run = () => {
    sheetIdle = 0;
    if (!S.gesture && anim.view === null) refreshSheet(false);
  };
  sheetIdle =
    typeof requestIdleCallback === 'function'
      ? requestIdleCallback(run, { timeout: 2000 })
      : (setTimeout(run, 300) as unknown as number);
}

/**
 * Cells on screen above which no fine detail is drawn — neither the codes nor the marks of a
 * locked symbol. One threshold for both, because it's one and the same leak: the codes are
 * NOT neutral, their shapes differ from one symbol to the next, so a field of them shades the
 * paper and the subject shows through the grain, exactly as marking every cell of a symbol
 * traced its contours. Both hand the drawing over before it's been filled.
 *
 * A count rather than a share of the grid: what makes a picture legible is how many cells the
 * eye takes in at once, not what fraction of the whole they represent — a 16-cell grid gives
 * away nothing even shown entirely, a 500-cell one gives away everything.
 *
 * 2500 (about 50×50) is under what it takes to make out a subject, and it happens to put a
 * cell at roughly 18 logical px on a phone, which is where a digit becomes comfortable to
 * read. The same number therefore serves legibility and secrecy at once. As a bonus it caps
 * the fillText calls per layer rebuild, the costliest thing in the loop below.
 *
 * Beyond it nothing at all is drawn on a cell left to fill — see the loop for why a neutral
 * placeholder is not an option.
 */
const DETAIL_MAX_CELLS = 2500;

const MONO_FONT = '"IBM Plex Mono", ui-monospace, monospace';

/**
 * The font of the code written on a cell left to fill, bounded by the cell's WIDTH as much as
 * by its height.
 *
 * The height alone was the rule, and it holds for as long as a cell is square. For a monospace
 * alphabet it is not: `asciiElargi` asks for a cell 0,6 as wide as it is tall — aspect(), and
 * that ratio is exactly what keeps the picture from being stretched — while its codes run up to
 * 22, so two digits. Measured on that cell, « 22 » at 0,5 × height came to 100 % of the width:
 * the number touched both grid lines, which is what makes it read as too big.
 *
 * TWO DIGITS, not the count of the code actually drawn: a size that changed from one cell to
 * the next would look worse than one slightly small, and NB_CODES caps every alphabet at two
 * digits anyway.
 *
 * The label is MEASURED rather than divided by an assumed advance. The stack falls back to
 * whatever monospace the platform has when IBM Plex Mono has not loaded yet, and that font's
 * advance is not ours to guess.
 *
 * A SQUARE CELL COMES OUT UNTOUCHED, and that is the point of taking the smaller of the two
 * bounds: at aspect 1 the widest label already fits in about two thirds of the width, so the
 * height rule stays the binding one and no other alphabet moves.
 */
function codeFont(c: CanvasRenderingContext2D, cellW: number, wanted: number): string {
  c.font = `${wanted}px ${MONO_FONT}`;
  // Le chiffre respire de part et d'autre au lieu de venir toucher les traits de la grille.
  const room = cellW * 0.82;
  const w = c.measureText('88').width;
  const size = w > room ? wanted * (room / w) : wanted;
  return `${size}px ${MONO_FONT}`;
}

/** La couche des cases porte-t-elle les numéros, au cadrage courant ? */
function codesVisible(): boolean {
  const v = cellWindow();
  // Pas de codes pendant le rejeu : ils désigneraient les cases pas encore révélées d'une
  // grille déjà finie, et sur une petite grille le chiffre resterait visible sous le motif
  // qui vient le recouvrir.
  // Ni pour un alphabet sans numéros (bareSheet) : la surbrillance de la couleur en cours, sous
  // le même seuil, en tient lieu.
  return !anim.replay && !alphabet().bareSheet && (v.r1 - v.r0) * (v.c1 - v.c0) <= DETAIL_MAX_CELLS;
}

/** Dessine la couche des cases, et rend si elle porte les numéros des cases à remplir.
 *  `only` restreint le dessin à une fenêtre de cases (cf. shiftBase) ; le choix des numéros, lui,
 *  reste celui de tout l'écran. */
function drawBaseOn(
  c: CanvasRenderingContext2D,
  only?: { r0: number; r1: number; c0: number; c1: number },
): boolean {
  const v = only ?? cellWindow();
  const mono = alphabet().id === 'ascii' ? 0.52 : 0.5;
  const font = codeFont(c, S.cellW, S.cellH * mono);
  const codesShown = codesVisible();
  if (alphabet().orderedPlay) {
    drawTapsOn(c, v, codesShown, font);
    return codesShown;
  }
  // L'ÉTAT DU TEXTE N'EST REPOSÉ QU'APRÈS UN GLYPHE, et non avant chaque numéro (2026-10-22).
  // Affecter `font` fait relire la chaîne de police au canvas, même quand elle ne change pas : posé
  // à chaque case, il doublait le prix des numéros — 11,8 ms contre 5,8 pour 800 numéros,
  // processeur ×4 — et les numéros sont le premier poste du dessin direct à ce zoom-là (104 ms
  // sur 184 au profileur, pendant un glissement en pointillisme riche). Seul un glyphe peut
  // déranger cet état (il pose ses propres couleurs), donc la suite des états vue par chaque
  // fillText et chaque glyphe est exactement celle d'avant : mêmes pixels, vérifié par
  // comparaison d'images sur les treize alphabets.
  let textStale = true;
  for (let r = v.r0; r < v.r1; r++) {
    for (let cc = v.c0; cc < v.c1; cc++) {
      if (anim.mask?.[r * S.gridW + cc]) continue; // blooming in: drawn live
      const x = cc * S.cellW;
      const y = r * S.cellH;
      const lvl = S.target[r][cc];
      if (S.filled[r][cc]) {
        if (lvl > 0) {
          drawGlyph(c, x, y, S.cellW, S.cellH, lvl);
          textStale = true;
        }
      } else if (codesShown) {
        if (textStale) {
          c.fillStyle = pal().code;
          c.font = font;
          c.textAlign = 'center';
          c.textBaseline = 'middle';
          textStale = false;
        }
        // Small baseline correction (the browser's "middle" rendering sits a bit high):
        // a constant screen pixel, otherwise it would grow with zoom like the grid lines above.
        c.fillText(String(lvl), x + S.cellW / 2, y + S.cellH / 2 + 1 / S.viewport.z);
      }
      // Zoomed out, a cell left to fill gets NOTHING. A neutral mark was tried and gives the
      // drawing away just as surely: resetPlay() counts code-0 cells as done, so they fall in
      // the branch above and stay bare, and a mark on every other cell outlines the picture's
      // whites in negative — a two-tone silhouette, plainly recognisable. Drawing nothing is
      // what makes an untouched cell and a blank one indistinguishable, so the sheet shows
      // only what the player has actually filled.
    }
  }
  return codesShown;
}

/** Les touches de S.taps[from, to) qui tombent dans la fenêtre `v`, dans l'ordre des taps — le
 *  corps de drawTapsOn, que l'affinage (sharpenSlice) parcourt par tranches. */
function drawTapsRange(
  c: CanvasRenderingContext2D,
  v: { r0: number; r1: number; c0: number; c1: number },
  from: number,
  to: number,
): void {
  for (let i = from; i < to; i++) {
    const idx = S.taps[i];
    const r = Math.floor(idx / S.gridW);
    const cc = idx % S.gridW;
    if (r < v.r0 || r >= v.r1 || cc < v.c0 || cc >= v.c1) continue;
    if (!S.filled[r][cc] || anim.mask?.[idx]) continue; // blooming in: drawn live
    const lvl = S.target[r][cc];
    if (lvl > 0) drawGlyph(c, cc * S.cellW, r * S.cellH, S.cellW, S.cellH, lvl);
  }
}

/**
 * drawBaseOn() pour un alphabet joué dans l'ordre (orderedPlay) : les cases peintes DANS L'ORDRE
 * OÙ LE JOUEUR LES A PEINTES — `S.taps`, pas la grille.
 *
 * Tout pour un alphabet qui déborde de sa case (peinture : la touche mord de ~0,6 case sur ses
 * voisines). Le parcours ligne par ligne décidait qui passe dessus par la PLACE des cases : la
 * touche qu'on venait de poser éclosait par-dessus tout (drawBlooms), puis, à la reconstruction,
 * retombait sous une voisine plus bas ou plus à droite peinte des minutes plus tôt. Signalé en
 * jouant (2026-10-13) : « on remplit une case, la touche est dessus, puis elle passe en
 * dessous ». Dans l'ordre des taps, la dernière touche posée reste dessus, comme sur une toile —
 * et c'est déjà l'ordre du rejeu (revealTaps).
 *
 * Réservé à ces alphabets-là : pour les autres l'ordre ne change rien de visible, sauf aux
 * hachures et à l'ASCII qui débordent eux aussi — et dont le dessin, depuis toujours ligne par
 * ligne, ne doit pas changer.
 *
 * Deux gardes : pendant le rejeu `S.taps` est COMPLET alors que `filled` n'en a reçu qu'un
 * préfixe, d'où la relecture de `filled` ; et une case de code 0 est « remplie » sans tap
 * (resetPlay), mais il n'y a rien à y dessiner.
 */
function drawTapsOn(
  c: CanvasRenderingContext2D,
  v: { r0: number; r1: number; c0: number; c1: number },
  codesShown: boolean,
  font: string,
): void {
  drawTapsRange(c, v, 0, S.taps.length);
  // Les codes APRÈS les touches : une touche qui déborde sur une case encore vide ne doit pas en
  // cacher le chiffre.
  if (!codesShown) return;
  c.fillStyle = pal().code;
  c.font = font;
  c.textAlign = 'center';
  c.textBaseline = 'middle';
  for (let r = v.r0; r < v.r1; r++)
    for (let cc = v.c0; cc < v.c1; cc++) {
      if (S.filled[r][cc]) continue;
      c.fillText(
        String(S.target[r][cc]),
        (cc + 0.5) * S.cellW,
        (r + 0.5) * S.cellH + 1 / S.viewport.z,
      );
    }
}

/**
 * Cells to highlight: a locked symbol marks ALL of its remaining cells, but only once few
 * enough of them are on screen (DETAIL_MAX_CELLS, the same gate as the codes above) —
 * otherwise the single cell under the cursor.
 *
 * Marking every cell is what you want while playing, and it is safe precisely because of that
 * gate: under it you can't make out the subject, and the codes are legible anyway, so the
 * marks add nothing you weren't already being shown. Without the gate it hands the drawing
 * over, since the cells of one symbol in a region ARE the local texture. Two attempts at
 * softening that without a zoom gate failed and aren't worth retrying — a travelling crest
 * only moves WHERE the leak happens, keeping full density inside the band, and thinning the
 * marks down to a scattered subset works but costs you the aid you locked the symbol for.
 */
function buildHalo(): void {
  haloCount = drawHaloOn(layerContext(haloCanvas, haloLayerViewport));
  haloReady = true;
}

function drawHaloOn(c: CanvasRenderingContext2D): number {
  const v = cellWindow();
  // La récolte owns this beat. The lock has already moved on to the next symbol, so leaving the
  // highlight up would light a second region, with its own wash and its own travelling sheen,
  // across the one the player is being shown — two lights, and the wrong one is the brighter.
  if (anim.flare) return 0;
  if (S.lockedSymbol !== null) {
    // off-screen, a highlighted cell isn't visible: only count what's visible
    if ((v.r1 - v.r0) * (v.c1 - v.c0) > DETAIL_MAX_CELLS) return 0;
    const lock = S.lockedSymbol;
    return paintHighlight(
      c,
      v,
      (r, cc) =>
        r >= 0 &&
        cc >= 0 &&
        r < S.gridH &&
        cc < S.gridW &&
        !S.filled[r][cc] &&
        S.target[r][cc] === lock,
    );
  }
  if (S.hover) {
    const [hr, hc] = S.hover;
    // A window of one cell: the hovered cell is the whole region, so it gets the four
    // sides of its own silhouette — the old per-cell outline, for free.
    return paintHighlight(
      c,
      { r0: hr, r1: hr + 1, c0: hc, c1: hc + 1 },
      (r, cc) => r === hr && cc === hc,
    );
  }
  return 0;
}

/** Large zone: instead of 14 000 canvas transforms per frame, we rasterize the zone
 *  once and only clip a growing disc afterward.
 *
 *  AU FIL DU FRONT, PAS D'UN COUP (2026-10-14). Toute la zone se dessinait dans l'image du tap :
 *  sur un fond de briques de 12 900 cases, 317 ms d'image figée au processeur ×4, la saccade
 *  ressentie en remplissant. Or le disque ne montre au début qu'une poignée de cases autour du
 *  doigt. Les cases sont donc rangées par distance au point touché (des tranches d'une case de
 *  large, sans tri) et chaque image dessine celles que le front va atteindre — growWave(). Le
 *  dessin de chaque case ne change pas, seul le moment où il est fait.
 *
 *  Réservé aux alphabets dont le dessin reste dans sa case (bakesOnTop) : l'ordre des cases n'est
 *  plus celui de la zone, et pour des hachures ou des caractères qui débordent il se verrait. Les
 *  autres dessinent toute la zone ici, comme avant. */
interface WaveOrder {
  /** La zone de la vague : gardée ici, parce que anim.wave est déjà effacée quand bakeCells
   *  vient finir la couche. */
  zone: Array<[number, number]>;
  /** Indices dans `zone`, tranche par tranche ; la tranche k va de start[k] à start[k + 1]. */
  idx: Uint32Array;
  start: Uint32Array;
  /** Largeur d'une tranche, en unités monde. */
  step: number;
  /** Tranches déjà dessinées dans la couche courante. */
  done: number;
  /** Fenêtre visible au cadrage de la couche : une case hors fenêtre tomberait hors du canevas. */
  win: { r0: number; r1: number; c0: number; c1: number };
}
let waveOrder: WaveOrder | null = null;

function buildWave(): void {
  const w = anim.wave!;
  const c1 = layerContext(waveCanvas, waveLayerViewport);
  layerContext(waveTintCanvas, waveLayerViewport);
  const cx = (w.c0 + 0.5) * S.cellW;
  const cy = (w.r0 + 0.5) * S.cellH;
  waveOrder = null;
  if (!alphabet().bakesOnTop) {
    let rmax = 0;
    for (const [r, c] of w.zone) {
      drawGlyph(c1, c * S.cellW, r * S.cellH, S.cellW, S.cellH, S.target[r][c]);
      const d = Math.hypot((c + 0.5) * S.cellW - cx, (r + 0.5) * S.cellH - cy);
      if (d > rmax) rmax = d;
    }
    tintWave(0, 0, waveTintCanvas.width, waveTintCanvas.height);
    w.rmax = rmax + Math.max(S.cellW, S.cellH);
    waveReady = true;
    return;
  }
  // Rangement par tranches de distance, en deux passes (compter, puis placer) : linéaire, là où
  // un tri de 13 000 distances coûterait lui-même une image.
  const step = Math.min(S.cellW, S.cellH);
  const n = w.zone.length;
  const band = new Uint32Array(n);
  let rmax = 0;
  let kmax = 0;
  for (let i = 0; i < n; i++) {
    const [r, c] = w.zone[i];
    const d = Math.hypot((c + 0.5) * S.cellW - cx, (r + 0.5) * S.cellH - cy);
    if (d > rmax) rmax = d;
    const k = Math.floor(d / step);
    band[i] = k;
    if (k > kmax) kmax = k;
  }
  const start = new Uint32Array(kmax + 2);
  for (let i = 0; i < n; i++) start[band[i] + 1]++;
  for (let k = 1; k < start.length; k++) start[k] += start[k - 1];
  const fill = start.slice(0, kmax + 1);
  const idx = new Uint32Array(n);
  for (let i = 0; i < n; i++) idx[fill[band[i]]++] = i;
  waveOrder = { zone: w.zone, idx, start, step, done: 0, win: cellWindow() };
  w.rmax = rmax + Math.max(S.cellW, S.cellH);
  waveReady = true;
}

/**
 * Dessine dans la couche de la vague toutes les cases à moins de `radius` du point touché, et
 * rafraîchit leur version teintée. Sans effet si la couche est déjà complète.
 */
function growWave(radius: number, aheadMs = 0): void {
  const o = waveOrder;
  if (!o) return;
  const last = o.start.length - 1;
  let kEnd = Math.min(last, Math.floor(radius / o.step) + 1);
  if (o.done >= kEnd && !aheadMs) return;
  const v = waveLayerViewport;
  const c1 = waveCanvas.getContext('2d')!;
  c1.setTransform(S.DPR, 0, 0, S.DPR, 0, 0);
  c1.translate(v.x, v.y);
  c1.scale(v.z, v.z);
  // Le rectangle des cases dessinées, pour n'y refaire que la teinte. Un seul par image : la
  // découper en bandes de lignes, pour moins de surface, coûtait PLUS (183 ms au pire au lieu de
  // 133, processeur ×4) — ce qui coûte est chaque copie de canevas à canevas, pas sa taille.
  let x0 = S.gridW;
  let x1 = -1;
  let y0 = S.gridH;
  let y1 = -1;
  const { win } = o;
  const drawBands = (k0: number, k1: number) => {
    for (let j = o.start[k0]; j < o.start[k1]; j++) {
      const [r, c] = o.zone[o.idx[j]];
      if (r < win.r0 || r >= win.r1 || c < win.c0 || c >= win.c1) continue;
      drawGlyph(c1, c * S.cellW, r * S.cellH, S.cellW, S.cellH, S.target[r][c]);
      if (c < x0) x0 = c;
      if (c > x1) x1 = c;
      if (r < y0) y0 = r;
      if (r > y1) y1 = r;
    }
  };
  const t0 = performance.now();
  if (o.done < kEnd) drawBands(o.done, kEnd);
  else kEnd = o.done;
  // D'avance, tant que l'image en a le temps : au début de la vague le front ne réclame qu'une
  // poignée de cases et l'image est légère. Sans ça, tout le travail tombe sur les images où le
  // front accélère — et une image lente fait avancer l'horloge d'autant, donc réclame plus de
  // cases à la suivante.
  if (aheadMs) {
    while (kEnd < last && performance.now() - t0 < aheadMs) {
      drawBands(kEnd, kEnd + 1);
      kEnd++;
    }
  }
  o.done = kEnd;
  if (x1 < 0) return;
  // Une case de marge de chaque côté et l'arrondi vers l'extérieur : l'anticrénelage d'une case
  // déborde d'un pixel, et la teinte doit couvrir tout ce que la couche a reçu.
  const k = v.z * S.DPR;
  const px0 = Math.max(0, Math.floor(v.x * S.DPR + (x0 - 1) * S.cellW * k));
  const py0 = Math.max(0, Math.floor(v.y * S.DPR + (y0 - 1) * S.cellH * k));
  const px1 = Math.min(waveTintCanvas.width, Math.ceil(v.x * S.DPR + (x1 + 2) * S.cellW * k));
  const py1 = Math.min(waveTintCanvas.height, Math.ceil(v.y * S.DPR + (y1 + 2) * S.cellH * k));
  if (px1 > px0 && py1 > py0) tintWave(px0, py0, px1 - px0, py1 - py0);
}

/** Temps qu'une image de la vague peut consacrer à dessiner d'avance, au-delà de ce que le
 *  front réclame. Un quart d'image à 60 Hz. */
const WAVE_AHEAD_MS = 4;

/**
 * "Fresh" version of the front: tints the already-drawn layer instead of redrawing everything —
 * same result, half the cost. Per pixel (a copy, then source-atop), so redoing any rectangle of it
 * from the current wave layer gives exactly what a whole-layer pass would: that is what lets
 * growWave() tint only what it has just drawn.
 */
function tintWave(x: number, y: number, w: number, h: number): void {
  const c2 = waveTintCanvas.getContext('2d')!;
  c2.save();
  c2.setTransform(1, 0, 0, 1, 0, 0);
  c2.clearRect(x, y, w, h);
  c2.drawImage(waveCanvas, x, y, w, h, x, y, w, h);
  c2.globalCompositeOperation = 'source-atop';
  c2.fillStyle = alphabet().selfColored ? pal().waveSelf : pal().wave;
  c2.fillRect(x, y, w, h);
  c2.restore();
}

// ---------- la récolte ----------
/**
 * The cells of a symbol that has just run out, ready for the light to cross them.
 *
 * Held in WORLD coordinates rather than rasterized into a layer, which is what lets the camera
 * pull back mid-effect without a rebuild — and what makes it free of the layer bookkeeping above.
 * Two things are precomputed once, for the same reason the wave rasterizes itself once: a walk
 * over the whole sheet is affordable at the instant the milestone fires, and not sixty times a
 * second.
 *
 *  - `path`: every cell of the symbol as ONE Path2D. Filled whole on every frame with a gradient
 *    along the diagonal, and that is the trick the effect rests on: a gradient varies with
 *    position, so a single fill draws the light, the pale wash behind it and the cells it has not
 *    reached yet, all at once. Per-cell alphas would mean one fill per cell — a thousand of them
 *    on an aplat.
 *  - `ks` / `rs` / `cs`: the same cells sorted by k = x + y, so the handful directly under the
 *    front can be found by dichotomy and have their glyph redrawn in fresh ink. That crest is the
 *    only per-cell cost here, and it is bounded by its own width rather than by the symbol's size.
 *
 * k = x + y throughout, as in drawHatch(): the light travels on the same 45° diagonal as the
 * hatching and the highlight's sheen, so the sheet only ever has one light. Working in k avoids
 * carrying √2 around.
 */
interface Harvest {
  code: number;
  path: Path2D;
  ks: Float64Array;
  rs: Int32Array;
  cs: Int32Array;
  kMin: number;
  kMax: number;
  /** k of the cell the last fill landed on: where the light is born. */
  kOrigin: number;
  /** Cell size it was built at — the world coordinates above are only valid for that one. */
  cellW: number;
  cellH: number;
}
let harvest: Harvest | null = null;

/** Glyphs redrawn in fresh ink per front, at most. The crest is the effect's only per-cell cost,
 *  and on a dense symbol a full one is over a thousand glyphs — the budget that made the bloom-in
 *  give way to a wave at 500 cells. Capped, it samples every nth cell of the crest instead: at
 *  this speed the eye reads a lit edge, not which cells of it are lit. */
const CREST_MAX = 200;

/** Part de l'éclosion pendant laquelle l'auréole d'encre se résorbe — voir drawBlooms(). */
const HALO_END = 0.7;

function buildHarvest(code: number): Harvest {
  const rs: number[] = [];
  const cs: number[] = [];
  const ks: number[] = [];
  const path = new Path2D();
  for (let r = 0; r < S.gridH; r++) {
    for (let c = 0; c < S.gridW; c++) {
      if (S.target[r][c] !== code) continue;
      rs.push(r);
      cs.push(c);
      ks.push((c + 0.5) * S.cellW + (r + 0.5) * S.cellH);
      path.rect(c * S.cellW, r * S.cellH, S.cellW, S.cellH);
    }
  }
  const ord = Array.from(ks.keys()).sort((a, b) => ks[a] - ks[b]);
  const n = ord.length;
  const h: Harvest = {
    code,
    path,
    ks: new Float64Array(n),
    rs: new Int32Array(n),
    cs: new Int32Array(n),
    kMin: n ? ks[ord[0]] : 0,
    kMax: n ? ks[ord[n - 1]] : 0,
    kOrigin: 0,
    cellW: S.cellW,
    cellH: S.cellH,
  };
  for (let i = 0; i < n; i++) {
    const j = ord[i];
    h.ks[i] = ks[j];
    h.rs[i] = rs[j];
    h.cs[i] = cs[j];
  }
  return h;
}

/** First index whose k is >= the target, in the sorted list. */
function lowerBound(ks: Float64Array, k: number): number {
  let lo = 0;
  let hi = ks.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (ks[mid] < k) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

/** Cells of the new symbol that have to be in frame for the player's own framing to be handed
 *  back untouched. One or two is a technicality — the eye has to land on work, not hunt for the
 *  single cell that happens to be in the corner. */
const HANDBACK_MIN = 8;
/** How dense a framing has to be, against the densest one on the sheet, to be worth landing on.
 *  Below 1 on purpose: a patch of work near where the player was reading beats a marginally
 *  richer one across the sheet, since what they have to re-find is the drawing around it. */
const HANDBACK_NEAR = 0.7;

/**
 * The viewport the harvest hands the sheet back to: the player's own zoom, moved onto cells the
 * symbol the lock has just taken still has to fill. null when `from` already frames enough of
 * them — i.e. when the right thing to do is give the framing back exactly as it was.
 *
 * A milestone empties a symbol and the lock moves straight on to the next (see onCodeProgress), so
 * the framing the player was working in is, at that exact moment, the one place on the sheet
 * guaranteed to hold nothing for them: they just finished it. Handing it back unchanged answers the
 * reward with an empty page, and the way out was a pinch out, a hunt, and a pinch back in.
 *
 * The zoom is never touched. It is the scale the player chose to play at; the beat is allowed to
 * move the camera, not to re-decide how close they work.
 */
export function handBackView(
  from: { z: number; x: number; y: number },
  code: number | null,
): { z: number; x: number; y: number } | null {
  if (code === null || !S.target.length) return null;
  const W = S.gridW;
  const H = S.gridH;
  // Remaining cells of the symbol as a summed-area table: the window slides over every position
  // on the grid below, and each count is then four reads instead of a walk over the window.
  const sw = W + 1;
  const sat = new Int32Array(sw * (H + 1));
  let left = 0;
  for (let r = 0; r < H; r++)
    for (let c = 0; c < W; c++) {
      const todo = S.target[r][c] === code && !S.filled[r][c] ? 1 : 0;
      left += todo;
      sat[(r + 1) * sw + c + 1] =
        sat[r * sw + c + 1] + sat[(r + 1) * sw + c] - sat[r * sw + c] + todo;
    }
  if (!left) return null;
  const count = (r0: number, c0: number, r1: number, c1: number): number =>
    sat[r1 * sw + c1] - sat[r0 * sw + c1] - sat[r1 * sw + c0] + sat[r0 * sw + c0];

  // The window `from` sees, in cells — measured on it and not with cellWindow(), which reads the
  // live viewport: during the harvest that one frames the whole sheet. Rounded INWARD, so every
  // cell counted is a cell actually in frame.
  const ww = Math.min(W, Math.max(1, Math.floor(S.availW / from.z / S.cellW)));
  const wh = Math.min(H, Math.max(1, Math.floor(S.availH / from.z / S.cellH)));
  const fc = Math.min(W - ww, Math.max(0, Math.round(-from.x / from.z / S.cellW)));
  const fr = Math.min(H - wh, Math.max(0, Math.round(-from.y / from.z / S.cellH)));
  const cur = count(fr, fc, fr + wh, fc + ww);
  // Zoomed out on a sheet with work everywhere, this is the common case and it costs one lookup.
  if (cur >= Math.min(HANDBACK_MIN, left)) return null;

  let bestN = 0;
  for (let r = 0; r + wh <= H; r++)
    for (let c = 0; c + ww <= W; c++) {
      const n = count(r, c, r + wh, c + ww);
      if (n > bestN) bestN = n;
    }
  const need = Math.max(1, Math.ceil(bestN * HANDBACK_NEAR));
  // The framing the player already had is as good as anything the sheet offers: travelling to
  // another one with the same handful of cells would be motion for nothing.
  if (cur >= need) return null;
  let br = fr;
  let bc = fc;
  let bd = Infinity;
  for (let r = 0; r + wh <= H; r++)
    for (let c = 0; c + ww <= W; c++) {
      if (count(r, c, r + wh, c + ww) < need) continue;
      const d = (r - fr) * (r - fr) + (c - fc) * (c - fc);
      if (d >= bd) continue;
      bd = d;
      br = r;
      bc = c;
    }

  // Centred on the window and not on the work inside it: the count is what was chosen, and a
  // window's centre is the only aim that keeps every cell it counted on screen.
  const fx = (bc + ww / 2) * S.cellW;
  const fy = (br + wh / 2) * S.cellH;
  return { z: from.z, x: S.availW / 2 - fx * from.z, y: S.availH / 2 - fy * from.z };
}

/**
 * The light itself: it leaves the cell the player just filled and runs BOTH ways along the
 * diagonal until it has left the sheet, lighting every cell of the finished symbol on its way and
 * leaving a pale wash behind it. Two fronts and not one because that is what ties the gesture to
 * the reward — the light comes out of the cell under the finger, not off the edge of the screen.
 *
 * Drawn on screen under the world transform, never into a layer: it moves every frame, and the
 * layers are a cache for what stands still.
 */
/**
 * L'ombre et la lumière de la récolte, débranchées le 2026-10-31 au profit des étincelles
 * (drawTwinkles) : voir la note de startHarvest dans ui.ts. Le recul, l'attente et le retour de la
 * caméra ne dépendent pas d'elles ; `true` ici les rallume telles qu'elles étaient.
 */
const HARVEST_LIGHT = false;

function drawHarvest(): void {
  const fl = anim.flare;
  if (!fl) {
    harvest = null;
    return;
  }
  const q = (anim.t - fl.t0) / FLARE_MS;
  if (q < 0) return; // the camera is still pulling back
  // Sans la lumière, rien à construire : le parcours des cases coûte une image sur 200 × 200.
  if (!HARVEST_LIGHT) return;
  // Built on the first frame of the light rather than when the flare was armed: that frame is
  // the one the milestone's own chord and burst are already on, and on a 200x200 sheet this walks
  // 40 000 cells.
  if (!harvest || harvest.code !== fl.code || harvest.cellW !== S.cellW) {
    harvest = buildHarvest(fl.code);
    harvest.kOrigin = (fl.c0 + 0.5) * S.cellW + (fl.r0 + 0.5) * S.cellH;
  }
  const h = harvest;
  if (!h.ks.length) return;

  // Same shape as the wave's front: quick out of the cell, settling as it leaves — an impulse and
  // not a scan. A linear pass reads as a machine sweeping the sheet.
  const B = Math.max(S.cellH * 6, (h.kMax - h.kMin) * 0.14);
  const travel = Math.max(h.kOrigin - h.kMin, h.kMax - h.kOrigin) + B;
  // THREE beats, and the middle one is the point: the fronts cross, then the whole harvest HOLDS
  // lit, then it goes. The first cut of this had the light still travelling when the fade began —
  // the front reached the far corner exactly as the wash started dropping — so the complete shape
  // was never on screen at once, and after the camera had pulled back to 3px cells there was
  // nothing left to look at. The hold is what makes the reward legible: it is the frame where the
  // player sees everything they painted, at rest, before the sheet is handed back.
  // inOut and not the wave's outCubic: over half of 1.5s, outCubic put the front 87% of the way
  // across in the first quarter of the crossing — the sweep was over before the eye had found it.
  // Here the light gathers, runs, and sets down into the hold as one gesture.
  const f = EASE.inOut(Math.min(1, q / FLARE_CROSS)) * travel;
  const fadeFrom = 1 - FLARE_TAIL_MS / FLARE_MS;
  const alpha = q > fadeFrom ? Math.max(0, (1 - q) / (1 - fadeFrom)) : 1;

  const g = S.g;
  const p = pal();

  // THE SHEET GOES INTO SHADOW, except the harvest. This is the one thing that makes the reward
  // legible once the camera has pulled back, and it took a measurement to find out why: at
  // whole-sheet zoom a cell is under 3 px, and at that size a per-cell WASH cannot draw a shape —
  // it averages with the paper between the cells and comes out as a flat tint over the whole
  // sheet. Raising its opacity does nothing for that (0.13 against 0.45 was, measured on the same
  // frame, a mean difference of 2 levels out of 255); putting the rest of the sheet in shadow is
  // 20 times that, because it is a large-area contrast of VALUE rather than a per-cell contrast of
  // hue. Same lesson as the highlight's own two-tone edge, one scale further out.
  //
  // ONE PLAIN RECTANGLE over the whole sheet, harvest included, and the wash below then lights the
  // harvest back out of it. The obvious way round — the sheet's rectangle with the cells punched
  // out of it by the even-odd rule — was written first and measured against this one, interleaved
  // in a single session: 3.9 ms of added paint against 1.8, for two frames whose mean luminance
  // differs by 3 levels out of 255. Even-odd has to walk four thousand subpaths to decide every
  // span; a rectangle has none. Dimming the harvest and lighting it again costs nothing, because
  // the light that does it was already being drawn.
  //
  // It closes in as the light crosses and lifts with everything else at the end: the shadow is the
  // light's own doing, not a curtain dropped over the game.
  {
    const shade = Math.min(1, q / FLARE_CROSS) * alpha;
    g.save();
    g.globalAlpha = shade;
    g.fillStyle = p.harvestShade;
    g.fillRect(-MARGIN, -MARGIN, S.pxW + 2 * MARGIN, S.pxH + 2 * MARGIN);
    g.restore();
  }

  // Gradient axis over the widest span the two fronts can reach, so no stop ever needs clamping
  // to the ends — a bright stop pinned at an edge would leave a lit border once the light is out.
  const kA = h.kOrigin - travel - B;
  const kB = h.kOrigin + travel + B;
  // A point (x, y) sits at (x + y) / 2 along the (1, 1) direction, hence the halves.
  const grd = g.createLinearGradient(kA / 2, kA / 2, kB / 2, kB / 2);
  let last = 0;
  const stop = (k: number, col: string) => {
    // Monotonic by construction: while f is smaller than the band the profile's own stops cross
    // each other, and the light is then a blob being born at the origin — which is what it is.
    last = Math.max(last, Math.min(1, (k - kA) / (kB - kA)));
    grd.addColorStop(last, col);
  };
  stop(h.kOrigin - f - B, p.harvestOff);
  stop(h.kOrigin - f, p.harvestOn);
  stop(h.kOrigin - f + B, p.harvestTail);
  stop(h.kOrigin, p.harvestTail);
  stop(h.kOrigin + f - B, p.harvestTail);
  stop(h.kOrigin + f, p.harvestOn);
  stop(h.kOrigin + f + B, p.harvestOff);

  g.save();
  g.globalAlpha = alpha;
  g.fillStyle = grd;
  g.fill(h.path);
  g.restore();

  // The crest: the ink itself heating up as the front reaches it, in the same fresh-to-graphite
  // move a cell makes when it is first laid down. The wash says which cells were yours; this is
  // what makes them look painted rather than tinted.
  const w = Math.max(S.cellH * 1.2, B * 0.22);
  crest(h, h.kOrigin - f, w, alpha);
  crest(h, h.kOrigin + f, w, alpha);
}

function crest(h: Harvest, kf: number, w: number, alpha: number): void {
  const lo = lowerBound(h.ks, kf - w);
  const hi = lowerBound(h.ks, kf + w);
  const n = hi - lo;
  if (n <= 0) return;
  const step = Math.max(1, Math.ceil(n / CREST_MAX));
  // The alphabets that carry their own colours are left alone: repainting a pigment in framboise
  // would put a colour on the sheet that the drawing does not contain. There the wash carries the
  // whole effect, which is also what the wave does with `waveSelf`.
  const tint = !alphabet().selfColored;
  const g = S.g;
  g.save();
  for (let i = lo; i < hi; i += step) {
    const d = Math.min(1, Math.abs(h.ks[i] - kf) / w); // 0 under the front, 1 at its edge
    g.globalAlpha = alpha * (1 - d * d);
    if (tint) setInkOverride(mixColor(pal().fresh, inkBase(), d));
    drawGlyph(g, h.cs[i] * S.cellW, h.rs[i] * S.cellH, S.cellW, S.cellH, h.code);
  }
  setInkOverride(null);
  g.restore();
}

// ---------- image ----------
// ---------- une image, un rendu ----------
/**
 * Demande un rendu pour l'image qui vient, au lieu de le faire tout de suite. Pour les gestes
 * continus — glisser, pincer, balayer —, dont les événements arrivent plusieurs fois par image.
 *
 * LE DOUBLON (mesuré le 2026-10-22, cf. PERF.md). Chaque `pointermove` appelait render() :
 *  - pendant un pincement, le navigateur livre un événement PAR DOIGT et par image, donc deux
 *    rendus par image — 61 pour 30 pas de pincement, sur toutes les feuilles essayées ;
 *  - pendant une animation (l'éclosion d'un tap dure une seconde, le fondu des compteurs, la
 *    boussole), la boucle de anim.ts dessine déjà chaque image, et le doigt en ajoutait un autre :
 *    un glissement de 30 pas juste après un tap doublait 14 à 27 images selon l'alphabet.
 * Mesuré au doigt simulé (Input.dispatchTouchEvent, donc des événements alignés sur les images
 * comme sur un téléphone), sur quatre alphabets : il n'en reste qu'une par geste, la « dernière
 * image propre » que la boucle d'animation dessine en s'arrêtant — cf. startLoop dans anim.ts.
 * La seconde image était perdue : seule la dernière d'une image atteint l'écran.
 *
 * SANS LATENCE. Le navigateur aligne les événements tactiles sur l'affichage et les livre AVANT
 * les rappels de requestAnimationFrame de la même image : le rendu demandé ici part dans l'image
 * même où le doigt a bougé, exactement comme le rendu immédiat d'avant.
 *
 * QUI DESSINE. Si la boucle d'animation tourne, son rappel est inscrit depuis l'image précédente,
 * donc passe avant le nôtre : elle dessine avec le cadrage déjà mis à jour, et le nôtre voit
 * `drawnSinceAsk` et s'abstient. Si elle dort — ou si elle s'est tue sans prévenir, cf. le
 * témoin de vie de anim.ts —, c'est le nôtre qui dessine. Un render() direct entre-temps (le
 * lâcher, endGesture) vaut aussi pour lui.
 */
let soonId = 0;
let drawnSinceAsk = true;
export function renderSoon(): void {
  drawnSinceAsk = false;
  if (soonId) return;
  soonId = requestAnimationFrame(() => {
    soonId = 0;
    if (!drawnSinceAsk) render();
  });
}

/**
 * Fait tout de suite le rendu demandé par renderSoon(), s'il n'a pas encore eu lieu.
 *
 * Pour les événements DISCRETS qui lisent le cadrage — un doigt qui se pose, un doigt qui se
 * lève. render() le borne (clampView), et ces événements l'ont toujours lu borné : le doigt qui
 * reste après un pincement s'ancre dessus (anchor), le pincement qui commence y prend son point
 * fixe. Sans ce passage, au bord de la feuille, ils s'ancreraient sur un cadrage que le rendu
 * suivant déplace, et l'image sauterait sous le doigt.
 */
export function renderPending(): void {
  if (!soonId) return;
  cancelAnimationFrame(soonId);
  soonId = 0;
  if (!drawnSinceAsk) render();
}

export function render(): void {
  const g = S.g;
  if (!g) return;
  drawnSinceAsk = true;
  const ar = alphabet().aspect();
  // The cell is as large as the box allows, in both directions: that's what makes a
  // landscape image fill the width without leaving bands.
  // The cell used to be floored to a whole physical pixel, to keep it on the screen's pixel
  // grid. Measured, that cost far more than it bought: the loss is the fractional part of the
  // scale, i.e. 1/scale of the width, and a dense grid on a phone is exactly where the scale
  // is small. A 200-wide grid in a 386pt box got 3.6px per cell, floored to 3 — 17% of the
  // sheet thrown away, and a blank band of paper around a picture already short of room. It
  // did not even buy the alignment it was for: S.DPR is capped at 2 while the screen is at
  // 2.625, so the backing store is resampled by 1.3125 and the "whole" pixels land between
  // the screen's own anyway. And at any zoom past 1 the viewport scale is fractional, so the
  // lattice is off-grid regardless — the floor only ever applied at zoom 1 exactly.
  // Fractional cells cost a full frame 1.6x, which is the 1.44x of sheet area they add.
  // the margin is included in the box: at zoom 1 the whole grid fits
  const usableW = Math.max(40, S.availW - 2 * MARGIN);
  const usableH = Math.max(40, S.availH - 2 * MARGIN);
  // AUCUN PLANCHER SUR L'ÉCHELLE, et c'est un invariant, pas une négligence : à zoom 1 la
  // feuille entière DOIT tenir dans la boîte. C'est la définition de ZOOM_FIT, celle vers
  // laquelle flyToWhole() ramène. Une feuille qui déborde à cette échelle-là oblige le joueur
  // à reculer sous l'ajustement pour la voir entière, c'est-à-dire à se débrouiller avec une
  // marge prévue pour le confort et pas pour rattraper un calcul de case.
  //
  // Il y avait ici un plancher de 3 px physiques par case, et il a coûté exactement ça sur les
  // grilles « Avancé » : à 250×250 sur un téléphone l'échelle naturelle vaut 2,90 px, le
  // plancher la remontait à 3, et la feuille passait à 375 pt de large dans une boîte de 362 —
  // 4 % de débordement, et 21 % sur une grille de 300 de large.
  //
  // Ce plancher protégeait d'un vrai défaut, mais qui n'était pas le sien : c'est le
  // `max(2, …)` sur la largeur qui déformait la case. Les deux côtés bornés SÉPARÉMENT
  // cessent d'être proportionnels — 2 × 3 pour un alphabet dont la case est carrée — et comme
  // une forme se dessine sur un côté de min(w, h), elle n'encrait que 2 px sur 3 : une rangée
  // de papier nu par case, lue comme des bandes pâles. En gardant l'échelle libre et le
  // rapport EXACT de l'alphabet, la case reste proportionnée à toute taille, et les bandes ne
  // peuvent pas revenir.
  const sDev = Math.min((usableW * S.DPR) / (S.gridW * ar), (usableH * S.DPR) / S.gridH);
  const hDev = sDev;
  const wDev = sDev * ar;
  const nW = wDev / S.DPR;
  const nH = hDev / S.DPR;
  if (nW !== S.cellW || nH !== S.cellH) {
    S.cellW = nW;
    S.cellH = nH;
    invalidateLayers();
  }
  S.pxW = S.cellW * S.gridW;
  S.pxH = S.cellH * S.gridH;
  S.sheet.classList.toggle('smooth', alphabet().id !== 'motifs');
  S.sheet.classList.toggle('pannable', S.viewport.z > 1);
  // the canvas is the size of the box: it's the window, not the drawing
  const devW = Math.round(S.availW * S.DPR);
  const devH = Math.round(S.availH * S.DPR);
  if (S.sheet.width !== devW || S.sheet.height !== devH) {
    S.sheet.width = devW;
    S.sheet.height = devH;
  }
  // Explicit CSS size: without it, the canvas takes its bitmap size as its intrinsic
  // size and displays at double on a DPR-2 screen.
  S.sheet.style.width = `${S.availW}px`;
  S.sheet.style.height = `${S.availH}px`;
  clampView();
  sizeLayers();

  // A camera flight moves the viewport every frame, exactly like a gesture: same live path,
  // and the cached layers are rasterized again once it lands.
  //
  // La détection est ICI, EN AMONT de la construction, et cet ordre est tout ce qui la rend
  // juste. Sous la composition — là où elle vivait — l'image d'atterrissage franchissait le
  // point de reconstruction avec des couches encore marquées prêtes, puis les recopiait
  // ÉTIRÉES depuis le viewport du décollage. Après un tap sur la boussole, l'étirement les
  // sort entièrement du cadre : la flèche ne s'affiche QUE si aucune case du symbole n'est en
  // vue, donc la destination d'un vol de boussole est garantie hors de l'écran de départ, et
  // ce que la couche contient est précisément cet écran-là. Il restait la feuille nue le temps
  // d'une image — un flash blanc, le dessin qui disparaît et revient. La fin d'un geste fait
  // le même passage direct → couches et n'a jamais clignoté : elle invalide AVANT d'appeler
  // render().
  const flying = anim.view !== null;
  if (wasFlying && !flying) invalidateLayers();
  wasFlying = flying;

  // Only outside a gesture: during a gesture the scene is drawn live, further below. Ni pendant
  // un vol, et pour la même raison : la branche directe ne lira aucune de ces couches, les
  // bâtir serait payer une rastérisation plein écran par image de vol — le prix exact que la
  // branche directe existe pour éviter.
  if (!S.gesture && !flying) {
    // Le décalage EN PREMIER : il arrondit le cadrage (cf. shiftBase), et les autres couches
    // doivent être prises au cadrage arrondi, sans quoi elles seraient étirées d'une fraction
    // de pixel dès l'image suivante.
    if (!baseReady && baseShiftable) shiftBase();
    baseShiftable = false;
    if (!gridReady) buildGrid();
    if (!baseReady) buildBase();
    if (!haloReady) buildHalo();
    if (anim.wave && !waveReady) buildWave();
  }

  // The counts stand in for the codes and the marks precisely when those are hidden, so they
  // are gated on the same threshold.
  if (!zonesReady) buildZones();
  {
    const vz = cellWindow();
    zonesShown = (vz.r1 - vz.r0) * (vz.c1 - vz.c0) > DETAIL_MAX_CELLS;
  }
  // La première leçon promet qu'un tap remplit une zone : elle attend donc que ce soit VRAI. À
  // l'échelle d'ouverture, un tap cadre la zone au lieu de la peindre (zonesOnScreen), et le
  // conseil arriverait juste avant que le jeu fasse autre chose que ce qu'il annonce.
  coachPeutPeindre(!zonesOnScreen());

  // Plates appearing counts as activity: they get their full spell on screen whatever brought
  // them back — a pinch, the zoom slider, a camera flight out of a zone. Without this, coming
  // back to the overview after a long stretch of painting would show them already faded, since
  // `act` would be as old as the last touch.
  {
    const shown = zonesOnScreen();
    if (shown && !anim.plates) animPoke();
    anim.plates = shown;
  }

  // Le repère ne réveille plus personne : il est figé (cf. drawMarkStatic), donc une feuille
  // marquée et immobile ne demande aucune image. C'était `anim.pulse`, la seule animation SANS
  // FIN du jeu — un symbole reste verrouillé pendant toute la partie — et donc la seule qui
  // empêchait la boucle rAF de s'endormir. Avec elle sont partis sheetCovered(), qui n'existait
  // que pour ne pas la laisser tourner sous le catalogue, et le réveil de closeHome().

  g.setTransform(1, 0, 0, 1, 0, 0);
  g.clearRect(0, 0, S.sheet.width, S.sheet.height);

  // Game over: the grid fades out and the image settles with a slight bounce. It
  // stays off afterward: bringing it back would re-clothe the drawing we just bared.
  let fin = 0;
  let ech = 1;
  // La partie est finie, mais pendant le rejeu la feuille se remplit : la grille reste
  // jusqu'au bout, et c'est la séquence de fin qui l'efface juste après.
  let frame = S.gameOver && !anim.replay ? 0 : 1;
  if (anim.final) {
    fin = Math.min(1, (anim.t - anim.final) / FINAL_MS);
    frame = 1 - EASE.outCubic(Math.min(1, fin * 2.2));
    ech = 1 + 0.035 * Math.sin(Math.min(1, fin * 1.6) * Math.PI);
  }
  const sec = animShakeOffset();
  const dx = sec ? sec.x : 0;
  const dy = sec ? sec.y : 0;

  const place = (
    cv: HTMLCanvasElement,
    alpha: number,
    viewport: { z: number; x: number; y: number },
  ) => {
    if (alpha <= 0) return;
    g.save();
    g.globalAlpha = alpha;
    if (ech !== 1) {
      g.translate(S.sheet.width / 2, S.sheet.height / 2);
      g.scale(ech, ech);
      g.translate(-S.sheet.width / 2, -S.sheet.height / 2);
    }
    applyStretch(viewport);
    g.drawImage(cv, dx * S.DPR, dy * S.DPR);
    g.restore();
  };

  // world reference frame (the ending bounce is centered on the screen, not on the
  // grid: it's the sheet settling that we see)
  const applyWorldTransform = () => {
    g.setTransform(S.DPR, 0, 0, S.DPR, 0, 0);
    if (ech !== 1) {
      g.translate(S.availW / 2, S.availH / 2);
      g.scale(ech, ech);
      g.translate(-S.availW / 2, -S.availH / 2);
    }
    g.translate(dx, dy);
    g.translate(S.viewport.x, S.viewport.y);
    g.scale(S.viewport.z, S.viewport.z);
  };

  // Under everything, and outside the two branches below: both draw the same sheet, only the
  // route to the grid on top of it differs.
  // save/restore is load-bearing, not hygiene: place() blits a screen-sized layer 1:1 and so
  // needs the identity transform set above. Leaving the world transform behind here drew every
  // layer magnified by DPR and offset by the viewport, which showed up as a band of stray
  // lattice below the sheet — invisible while the paper covered the whole box.
  g.save();
  applyWorldTransform();
  // Le passe-partout AVANT le papier, donc dessous : c'est un montage, la feuille est posée
  // dessus. Ouvert par la séquence finale, puis tenu tant que la feuille reste terminée — même
  // condition que la grille qui reste éteinte.
  if (S.gameOver && !anim.replay) {
    paintMat(g, anim.final ? EASE.outCubic(Math.min(1, fin * 2.4)) : 1);
  }
  paintPaper(g);
  g.restore();

  if (S.gesture || flying) {
    // Direct draw: one clear and three passes, instead of fourteen screens' worth
    // through the layers (measured at 40ms per frame on a Pixel 7a). The layers are
    // useless here since the viewport changes every frame.
    applyWorldTransform();
    {
      g.save();
      g.globalAlpha = frame;
      drawGridOn(g);
      g.restore();
    }
    if (!drawSheetImage(g, ech)) drawBaseOn(g);
    g.save();
    g.globalAlpha = haloAlpha();
    haloCount = drawHaloOn(g);
    g.restore();
  } else {
    scheduleSheet();
    place(gridCanvas!, frame, gridLayerViewport);
    place(baseCanvas, 1, baseLayerViewport);
    if (anim.wave && waveReady) drawWave(dx, dy, ech);
    // Le repère est ENTIER dans sa couche — plus rien ne se pose par-dessus sur la vitre, il
    // n'a plus de part animée (cf. drawMarkStatic). C'est ce qui permet à la boucle de dormir.
    // Son alpha ne vaut moins de 1 que pendant le fondu qui suit un palier (cf. haloAlpha).
    if (haloCount) place(haloCanvas, haloAlpha(), haloLayerViewport);
    applyWorldTransform();
  }

  // Over the ink and under the local celebration: the light passes across the drawing, the ring
  // and the scraps of the cell that earned it stay on top of everything.
  drawHarvest();
  drawBlooms();
  drawReplayTrail();
  drawRings();
  drawZones();
  drawBursts();
  drawTwinkles();
  drawMistake();

  // Final light sweep: clipped to ink already laid down (source-atop), so it's the
  // symbols that light up, not the background.
  if (fin > 0 && fin < 1) {
    const p = EASE.inOut(Math.min(1, fin * 1.25));
    const band = S.pxW * 0.55;
    const cx = -band + p * (S.pxW + band * 2);
    const grd = g.createLinearGradient(cx - band / 2, 0, cx + band / 2, S.pxH);
    grd.addColorStop(0, pal().sweepOff);
    grd.addColorStop(0.5, pal().sweepOn);
    grd.addColorStop(1, pal().sweepOff);
    g.save();
    g.globalCompositeOperation = 'source-atop';
    g.fillStyle = grd;
    g.fillRect(0, 0, S.pxW, S.pxH);
    g.restore();
  }

  // En dernier et SANS la transformation monde : la boussole est posée sur la vitre, pas sur
  // la feuille. Elle ne bouge pas avec le dessin, elle montre où il continue.
  g.setTransform(S.DPR, 0, 0, S.DPR, 0, 0);
  drawGuide();
}

// ---------- la boussole ----------
/**
 * Une flèche au bord de l'écran quand la vue ne contient AUCUNE case du symbole verrouillé.
 *
 * Le cas qu'elle règle : verrouiller un symbole, zoomer pour peindre, et se retrouver devant
 * une portion de feuille où il n'y a rien à faire. Le surlignage ne peut rien dire — il montre
 * des cases, et il n'y en a pas ici. Il fallait dézoomer, chercher, rezoomer.
 *
 * Elle vit dans la langue des compteurs de zone — plaque couleur papier, signe à l'accent
 * profond — parce qu'elle est le même genre d'objet : une note posée sur la feuille, jamais un
 * des symboles qui composent le dessin. Un disque et non une plaque rectangulaire, pour qu'on
 * ne la confonde pas avec un compteur.
 *
 * SA TAILLE NE DIT RIEN. Elle a d'abord grandi avec la proximité de la cible, et c'était une
 * information de trop : un bouton dont la surface tactile change de taille sous le doigt selon
 * l'endroit où l'on se trouve se vise moins bien, et la distance se lit déjà — la flèche
 * pointe, on y va, on voit. Un seul rayon, donc, et une cible tactile constante.
 */
const GUIDE_R = 26;
/** Marge entre la boussole et le bord du canevas. */
const GUIDE_PAD = 10;
/** Le fondu, à l'apparition comme à la disparition. Court : c'est un objet qui doit se
 *  remarquer, pas s'annoncer. Assez long tout de même pour qu'il ne CLIGNOTE pas quand le doigt
 *  fait passer la dernière case de part et d'autre du bord. */
const GUIDE_FADE_MS = 150;

/**
 * La tolérance : de combien on rentre depuis le bord avant de décréter qu'une case « est à
 * l'écran ».
 *
 * DEUX RÈGLES, et la première fait presque tout le travail : une case ne compte que si elle tient
 * ENTIÈREMENT dans la vue. Une case coupée par le bord ne se voit pas, elle se devine — et c'est
 * elle qui faisait taire la flèche devant ce qui ressemble à une portion vide.
 *
 * La marge par-dessus vaut un tiers de case, et elle est petite exprès. Elle a d'abord valu le
 * diamètre de la boussole, pour qu'une case cachée dessous ne compte pas, et ce raisonnement
 * était faux : si la case compte comme visible, la flèche ne se dessine pas, donc rien ne la
 * cache. Il n'en restait que trois bonnes cases de bande morte tout autour, et la flèche
 * s'affichait devant des écrans où il y avait manifestement du travail.
 *
 * En cases et non en pixels, parce que c'est la case qui est l'unité de ce que le joueur cherche :
 * la même marge doit valoir « à peine au bord » au zoom où l'on peint comme au zoom où l'on
 * survole. Bornée par le haut pour qu'un très gros zoom n'en fasse pas une bande, et par une
 * fraction de l'écran pour qu'un canevas minuscule garde un intérieur non vide.
 */
function guideInset(): number {
  const cell = Math.min(S.cellW, S.cellH) * S.viewport.z;
  return Math.min(Math.max(6, cell * 0.35), 16, S.availW * 0.1, S.availH * 0.1);
}

/** Ce que la boussole montre, recalculé à chaque image : cellule visée, et sa place à l'écran. */
type Guide = { r: number; c: number; x: number; y: number; angle: number };
let guide: Guide | null = null;
/** La dernière boussole affichée, gardée pendant qu'elle s'efface : sans elle, une flèche qui
 *  disparaît n'aurait plus ni position ni direction à dessiner, et sauterait au lieu de partir. */
let guideShown: Guide | null = null;
let guideAlpha = 0;
let guideClock = 0;

/** De combien la cible tactile déborde le disque. Le doigt vise plus gros qu'il ne voit, et la
 *  boussole ne s'affiche QUE lorsqu'aucune case du symbole n'est entièrement visible : ce
 *  débordement ne prend donc à personne un tap qui allait peindre — ce qu'il recouvre n'est, par
 *  construction, pas ce que le joueur cherche. */
const GUIDE_TAP_PAD = 12;

/** L'opacité à partir de laquelle le disque compte comme PRÉSENT sous le doigt. Pas zéro : une
 *  ombre de flèche qui répond encore serait aussi surprenante qu'un bouton qui ne répond plus. */
const GUIDE_TAP_ALPHA = 0.35;

/**
 * La cellule que la boussole désigne, ou null si le doigt n'est pas dessus. Lue par le tap.
 *
 * Ce qui répond au doigt est ce qui SE VOIT, et non ce qui est calculé. Pendant le fondu de
 * disparition — 150 ms, le temps qu'une case du symbole entre dans la vue — `guide` est déjà nul
 * alors que le disque est toujours à l'écran : le tap le traversait et peignait la case dessous.
 * On répond donc aussi pour `guideShown` tant qu'il reste visible, avec la cible qu'il portait,
 * qui est encore une case à peindre.
 */
export function guideHit(x: number, y: number): [number, number] | null {
  const g = guide ?? (guideAlpha > GUIDE_TAP_ALPHA ? guideShown : null);
  if (!g) return null;
  const d = Math.hypot(x - g.x, y - g.y);
  return d <= GUIDE_R + GUIDE_TAP_PAD ? [g.r, g.c] : null;
}

function findGuide(): void {
  guide = null;
  // Le réglage du joueur, et testé AVANT tout le reste parce que c'est le test le moins cher :
  // boussole coupée, on ne paie même pas le balayage de la grille plus bas.
  if (!settings().compass) return;
  const lock = S.lockedSymbol;
  // Pas pendant que le repère est retenu (anim.haloHeld) : elle désigne les cases d'un symbole
  // qu'on ne montre pas encore, et à la fin de la pièce d'initiation elle surgissait avec sa
  // bulle dans le temps mort de la bascule, avant le recul (relevé en capture le 2026-10-31).
  if (lock === null || !S.target.length || anim.flare || anim.replay || anim.haloHeld) return;

  // D'abord : y a-t-il du travail SOUS LES YEUX ? La réponse est presque toujours oui, et elle
  // sort au premier candidat — c'est ce qui fait que le balayage complet plus bas ne se paie
  // que dans le cas où la boussole sert vraiment.
  //
  // « Sous les yeux » veut dire ENTIÈREMENT dans le rectangle rentré de guideInset() : une case
  // à cheval sur le bord ne se voit pas, elle se devine.
  const v = cellWindow();
  const z = S.viewport.z;
  const m = guideInset();
  const inX0 = (m - S.viewport.x) / z;
  const inX1 = (S.availW - m - S.viewport.x) / z;
  const inY0 = (m - S.viewport.y) / z;
  const inY1 = (S.availH - m - S.viewport.y) / z;
  for (let r = Math.max(0, v.r0); r < Math.min(S.gridH, v.r1); r++) {
    const y = r * S.cellH;
    if (y < inY0 || y + S.cellH > inY1) continue;
    const row = S.target[r];
    const fil = S.filled[r];
    for (let c = Math.max(0, v.c0); c < Math.min(S.gridW, v.c1); c++) {
      if (row[c] !== lock || fil[c]) continue;
      const x = c * S.cellW;
      if (x >= inX0 && x + S.cellW <= inX1) return;
    }
  }

  // La plus proche du CENTRE de la vue, en cellules — la métrique du monde et non de l'écran,
  // pour que la cible ne change pas en zoomant sur place.
  const ccx = (S.availW / 2 - S.viewport.x) / z / S.cellW;
  const ccy = (S.availH / 2 - S.viewport.y) / z / S.cellH;
  let br = -1;
  let bc = -1;
  let best = Infinity;
  for (let r = 0; r < S.gridH; r++) {
    const row = S.target[r];
    const dy = r + 0.5 - ccy;
    const dy2 = dy * dy;
    // Une ligne entière est déjà plus loin que le meilleur candidat : rien à y chercher.
    if (dy2 >= best) continue;
    for (let c = 0; c < S.gridW; c++) {
      if (row[c] !== lock || S.filled[r][c]) continue;
      const dx = c + 0.5 - ccx;
      const d = dx * dx + dy2;
      if (d < best) {
        best = d;
        br = r;
        bc = c;
      }
    }
  }
  if (br < 0) return; // symbole terminé : la palette s'en occupe

  const tx = (bc + 0.5) * S.cellW * z + S.viewport.x;
  const ty = (br + 0.5) * S.cellH * z + S.viewport.y;
  const mx = S.availW / 2;
  const my = S.availH / 2;
  const vx = tx - mx;
  const vy = ty - my;
  if (Math.hypot(vx, vy) < 1) return;

  // Où le rayon sort du rectangle de l'écran, rentré de la marge : la flèche se pose sur le
  // bord VERS lequel il faut aller, et glisse le long quand on se déplace.
  const ix = mx - GUIDE_R - GUIDE_PAD;
  const iy = my - GUIDE_R - GUIDE_PAD;
  const k = Math.min(ix / Math.abs(vx || 1e-6), iy / Math.abs(vy || 1e-6));
  guide = { r: br, c: bc, x: mx + vx * k, y: my + vy * k, angle: Math.atan2(vy, vx) };
}

/**
 * Où poser la vue quand on tape la boussole.
 *
 * Centrer la case visée, ce que faisait le vol d'origine, répond à la lettre de la flèche et
 * manque son intention : la case désignée est la PLUS PROCHE, presque jamais la seule, et
 * l'amener au centre pouvait laisser ses voisines juste derrière le bord — un tap de plus, et
 * la même flèche revenait. On cherche donc, parmi tous les cadrages qui contiennent la case
 * promise, celui qui montre le plus de cases du symbole.
 *
 * La fenêtre se compte en cases ENTIÈREMENT visibles, marge de guideInset() comprise : c'est
 * exactement le critère qui fait disparaître la boussole, donc après le vol elle s'en va —
 * elle ne resterait pas à désigner une case qu'on voit déjà.
 *
 * Une somme préfixe sur la seule région utile — au plus quatre fenêtres — rend chaque cadrage
 * candidat en temps constant, et les candidats sont bornés par la taille de la fenêtre. C'est
 * payé une fois, sur un tap, jamais dans une image.
 */
function guideFraming(tr: number, tc: number, lock: number): { x: number; y: number } {
  const z = S.viewport.z;
  const m = guideInset();
  const w = Math.max(1, Math.floor((S.availW - 2 * m) / z / S.cellW));
  const h = Math.max(1, Math.floor((S.availH - 2 * m) / z / S.cellH));

  // Les coins haut-gauche possibles : la fenêtre doit contenir la case visée — la flèche vient
  // de la promettre, le cadrage n'a pas le droit de la perdre — et ne pas sortir de la feuille.
  const aLo = Math.max(0, tr - h + 1);
  const aHi = Math.min(S.gridH - h, tr);
  const bLo = Math.max(0, tc - w + 1);
  const bHi = Math.min(S.gridW - w, tc);
  // Plus grande que la feuille dans un sens : rien à chercher dans ce sens, tout y tient déjà.
  const libreY = aHi < aLo;
  const libreX = bHi < bLo;
  const a0 = libreY ? Math.round((S.gridH - h) / 2) : aLo;
  const a1 = libreY ? a0 : aHi;
  const b0 = libreX ? Math.round((S.gridW - w) / 2) : bLo;
  const b1 = libreX ? b0 : bHi;
  if (libreY && libreX) return { x: (S.gridW / 2) * S.cellW, y: (S.gridH / 2) * S.cellH };

  const r0 = Math.max(0, a0);
  const r1 = Math.min(S.gridH, a1 + h);
  const c0 = Math.max(0, b0);
  const c1 = Math.min(S.gridW, b1 + w);
  const nr = r1 - r0;
  const nc = c1 - c0;
  const W = nc + 1;
  const P = new Int32Array((nr + 1) * W);
  for (let r = 0; r < nr; r++) {
    const row = S.target[r0 + r];
    const fil = S.filled[r0 + r];
    let acc = 0;
    for (let c = 0; c < nc; c++) {
      if (row[c0 + c] === lock && !fil[c0 + c]) acc++;
      P[(r + 1) * W + c + 1] = P[r * W + c + 1] + acc;
    }
  }
  const compte = (a: number, b: number): number => {
    const ya = Math.max(0, a - r0);
    const yb = Math.min(nr, a - r0 + h);
    const xa = Math.max(0, b - c0);
    const xb = Math.min(nc, b - c0 + w);
    if (yb <= ya || xb <= xa) return 0;
    return P[yb * W + xb] - P[ya * W + xb] - P[yb * W + xa] + P[ya * W + xa];
  };

  let bestA = a0;
  let bestB = b0;
  let bestN = -1;
  let bestD = Infinity;
  for (let a = a0; a <= a1; a++) {
    for (let b = b0; b <= b1; b++) {
      const n = compte(a, b);
      // À nombre égal, le cadrage qui met la case promise le plus près du centre : sans ce
      // départage le premier candidat gagne, et elle se retrouve collée contre un bord.
      const d = Math.abs(tr + 0.5 - (a + h / 2)) + Math.abs(tc + 0.5 - (b + w / 2));
      if (n > bestN || (n === bestN && d < bestD)) {
        bestN = n;
        bestD = d;
        bestA = a;
        bestB = b;
      }
    }
  }
  return { x: (bestB + w / 2) * S.cellW, y: (bestA + h / 2) * S.cellH };
}

/** Le vol lancé par un tap sur la boussole. Voir guideFraming : ce n'est pas un centrage. */
export function flyToGuide(r: number, c: number): void {
  coachReussi('boussole');
  const lock = S.lockedSymbol;
  if (lock === null) {
    flyToCell(r, c);
    return;
  }
  const z = S.viewport.z;
  const p = guideFraming(r, c, lock);
  animFlyTo(z, S.availW / 2 - p.x * z, S.availH / 2 - p.y * z);
}

function drawGuide(): void {
  findGuide();
  if (guide) guideShown = guide;
  // Le seul endroit du code qui sache à la fois que la flèche est là et OÙ elle est. On passe
  // la position du DISQUE et non la case visée : celle-ci est hors écran par définition — c'est
  // la condition même qui affiche la flèche. Deux comparaisons quand il n'y a rien à faire.
  coachBoussole(guide && { x: guide.x, y: guide.y });

  // Le fondu. Borné à 80 ms d'écart entre deux images, sinon un retour d'arrière-plan — où le
  // temps a continué de courir sans qu'une seule image soit rendue — le ferait sauter d'un coup.
  const dt = Math.min(80, Math.max(0, anim.t - guideClock));
  guideClock = anim.t;
  const vise = guide ? 1 : 0;
  if (noMotion()) guideAlpha = vise;
  else {
    const pas = dt / GUIDE_FADE_MS;
    guideAlpha += Math.max(-pas, Math.min(pas, vise - guideAlpha));
  }
  // Il reste du fondu à jouer : la boucle doit tourner quelques images de plus que le geste.
  anim.guide = guideAlpha !== vise;
  if (anim.guide) animWake();

  if (guideAlpha <= 0.001 || !guideShown) {
    guideShown = guide;
    return;
  }
  const g = S.g;
  const { x, y, angle } = guideShown;
  const p = pal();
  g.save();
  g.globalAlpha = guideAlpha;
  g.translate(x, y);

  // Une ombre courte, comme celle qui décolle la feuille de la scène : sans elle le disque est
  // posé À PLAT sur le papier et se lit comme une case peinte en jaune.
  g.fillStyle = p.guideShadow;
  g.beginPath();
  g.arc(0, GUIDE_R * 0.14, GUIDE_R, 0, 7);
  g.fill();

  g.beginPath();
  g.arc(0, 0, GUIDE_R, 0, 7);
  g.fillStyle = p.guide;
  g.fill();
  g.lineWidth = 1.5;
  g.strokeStyle = p.guideEdge;
  g.stroke();

  // Une flèche avec sa hampe, et non un simple chevron : essayé, le chevron seul se lit comme
  // un COIN dès qu'il pointe en diagonale — deux traits à angle droit ne disent pas une
  // direction. La hampe tranche l'ambiguïté, et c'est aussi le signe que le jeu emploie déjà
  // pour revenir au catalogue.
  g.rotate(angle);
  const s = GUIDE_R * 0.58;
  g.strokeStyle = p.guideSign;
  g.lineWidth = Math.max(2.5, GUIDE_R * 0.17);
  g.lineCap = 'round';
  g.lineJoin = 'round';
  g.beginPath();
  g.moveTo(-s, 0);
  g.lineTo(s * 0.72, 0);
  g.moveTo(s * 0.05, -s * 0.62);
  g.lineTo(s * 0.75, 0);
  g.lineTo(s * 0.05, s * 0.62);
  g.stroke();
  g.restore();
}

/** Clipped in screen space (the layers are blitted without transform): two arcs in
 *  opposite directions give the wave front's ring. */
function drawWave(dx: number, dy: number, ech: number): void {
  const g = S.g;
  const w = anim.wave!;
  const p = Math.min(1, (anim.t - w.t0) / w.dur);
  const R = EASE.outCubic(p) * w.rmax;
  const front = Math.max(S.cellH * 2, w.rmax * 0.13);
  // Le front montre jusqu'à R ; une case dont le centre est à R + une diagonale y a encore des
  // pixels. Deux côtés de case couvrent cette diagonale.
  growWave(R + 2 * Math.max(S.cellW, S.cellH), WAVE_AHEAD_MS);
  const cx = (S.viewport.x + (w.c0 + 0.5) * S.cellW * S.viewport.z + dx) * S.DPR;
  const cy = (S.viewport.y + (w.r0 + 0.5) * S.cellH * S.viewport.z + dy) * S.DPR;
  const rz = S.viewport.z * S.DPR;

  const placeClipped = (cv: HTMLCanvasElement, rExt: number, rInt: number) => {
    if (rExt <= 0) return;
    g.save();
    if (ech !== 1) {
      g.translate(S.sheet.width / 2, S.sheet.height / 2);
      g.scale(ech, ech);
      g.translate(-S.sheet.width / 2, -S.sheet.height / 2);
    }
    // the clip is computed in the current viewport, so applied before the layer's stretch
    g.beginPath();
    g.arc(cx, cy, rExt * rz, 0, 7);
    if (rInt > 0) g.arc(cx, cy, rInt * rz, 0, 7, true); // reverse direction: hole in the middle
    g.clip();
    applyStretch(waveLayerViewport);
    g.drawImage(cv, dx * S.DPR, dy * S.DPR);
    g.restore();
  };
  placeClipped(waveCanvas, R - front, 0);
  placeClipped(waveTintCanvas, R, Math.max(0, R - front));
}

function drawBlooms(): void {
  const g = S.g;
  const tint = !alphabet().selfColored;
  for (const p of anim.pend) {
    const ph = animPhase(p.t0);
    if (ph < 0) continue; // not blooming yet
    const lvl = S.target[p.r][p.c];
    if (!lvl) continue;
    const x = p.c * S.cellW;
    const y = p.r * S.cellH;
    const cx = x + S.cellW / 2;
    const cy = y + S.cellH / 2;

    // L'AURÉOLE : la même forme, débordée, qui se résorbe — l'encre qui prend dans le papier
    // une fraction de seconde avant de s'y fixer. Découpée à sa case, sans quoi chaque case
    // bave sur ses voisines et une zone entière vire au rose d'un coup.
    //
    // Pas d'auréole pour un alphabet qui porte ses propres couleurs : chez le pointilliste le
    // pigment EST le symbole, et le teinter en rose dirait le contraire de ce que l'alphabet
    // raconte. Même raison que le `tint` ci-dessous.
    if (tint && ph < HALO_END) {
      const q = ph / HALO_END;
      const b = 1.5 - 0.5 * EASE.outCubic(q);
      g.save();
      g.beginPath();
      g.rect(x, y, S.cellW, S.cellH);
      g.clip();
      g.globalAlpha = (1 - q) * 0.28;
      g.translate(cx, cy);
      g.scale(b, b);
      g.translate(-cx, -cy);
      setInkOverride(pal().fresh);
      drawGlyph(g, x, y, S.cellW, S.cellH, lvl);
      setInkOverride(null);
      g.restore();
    }

    // LA PRESSE. La forme arrive à sa taille pleine et s'écrase d'un cheveu avant de reprendre
    // son aplomb : le geste d'un tampon qu'on pose, là où l'éclosion précédente faisait grandir
    // la forme depuis rien en la faisant tourner. Deux raisons de l'avoir remplacée : sur une
    // zone contiguë remplie d'un coup, des dizaines de formes qui tournent chacune dans son
    // sens donnent un fourmillement dont l'œil ne tire aucune information ; et une forme qui
    // grandit depuis zéro ment sur ce qui se passe — la case n'a pas changé de taille, elle a
    // reçu de l'encre.
    const e = EASE.outBack(Math.min(1, ph * 1.15));
    g.save();
    g.globalAlpha = Math.min(1, ph * 3.2);
    g.translate(cx, cy);
    g.scale(1 + (1 - e) * 0.16, 0.7 + 0.3 * e); // écrasée, puis d'aplomb
    g.translate(-cx, -cy);
    // the ink arrives in the accent and cools toward graphite: the cell "heats up" on contact
    if (tint && ph < 0.7) setInkOverride(mixColor(pal().fresh, inkBase(), ph / 0.7));
    drawGlyph(g, x, y, S.cellW, S.cellH, lvl);
    setInkOverride(null);
    g.restore();
  }
}

function drawRings(): void {
  const g = S.g;
  for (const a of anim.rings) {
    const p = (anim.t - a.t0) / RING_MS;
    if (p < 0 || p >= 1) continue;
    const rr = Math.max(S.cellW, S.cellH) * (0.4 + EASE.outQuint(p) * 4.2);
    g.save();
    g.globalAlpha = (1 - p) * 0.75;
    g.strokeStyle = a.col ?? pal().ring;
    g.lineWidth = (Math.max(1, S.cellH * 0.22) * (1 - p)) / S.viewport.z + 0.5;
    g.beginPath();
    g.arc(a.x, a.y, rr, 0, 7);
    g.stroke();
    g.restore();
  }
}

/**
 * Les étincelles d'un palier (2026-10-31) : pendant la récolte, la caméra reculée sur la feuille
 * entière, chaque case du symbole qu'on vient de finir scintille à son tour, en vague depuis la
 * case qui l'a terminé (cf. harvestTwinkles dans ui.ts).
 *
 * Elles remplacent la lumière de la récolte (HARVEST_LIGHT), qui assombrissait toute la feuille
 * pour éclairer le symbole — un clignotement de la page entière. Ici rien ne s'éteint : la fête se
 * pose PAR-DESSUS le dessin, et dit « tout ça, c'était toi » en le faisant briller.
 *
 * Une étoile dorée à quatre branches fines, au cœur blanc, dans un halo doré qui s'estompe vers
 * l'extérieur. Le premier essai — étoile blanche sur un disque plein de l'accent — se lisait, en
 * capture, comme des pastilles violettes barrées : un disque à bord net est un objet, un halo qui
 * s'efface est une lumière. L'or et non l'accent : c'est la couleur que tout le monde lit comme
 * une récompense, et elle tient sur la peinture comme sur le papier. Une taille plancher à
 * l'écran, sinon dézoomé une étincelle de case de 3 px ne se voit pas.
 */
function drawTwinkles(): void {
  if (!anim.twinkles.length) return;
  const g = S.g;
  const cell = Math.max(S.cellW, S.cellH);
  const floor = 14 / S.viewport.z;
  for (const w of anim.twinkles) {
    const p = (anim.t - w.t0) / TWINKLE_MS;
    if (p < 0 || p >= 1) continue;
    // S'ouvre vite, se referme plus lentement : un éclat, pas un battement.
    const k = p < 0.3 ? EASE.outQuint(p / 0.3) : 1 - EASE.inOut((p - 0.3) / 0.7);
    const R = Math.max(cell * 1.1, floor) * w.s * k;
    if (R <= 0.01) continue;
    g.save();
    g.translate(w.x, w.y);
    const halo = g.createRadialGradient(0, 0, 0, 0, 0, R * 0.8);
    halo.addColorStop(0, `rgba(255, 214, 102, ${0.55 * k})`);
    halo.addColorStop(1, 'rgba(255, 214, 102, 0)');
    g.fillStyle = halo;
    g.beginPath();
    g.arc(0, 0, R * 0.8, 0, 7);
    g.fill();
    g.rotate(w.rot + p * 0.8);
    g.globalAlpha = Math.min(1, k * 1.4);
    star(g, R, R * 0.13, '#ffc93c');
    star(g, R * 0.55, R * 0.1, '#fffbea');
    g.restore();
  }
}

function star(g: CanvasRenderingContext2D, R: number, r: number, fill: string): void {
  g.fillStyle = fill;
  g.beginPath();
  for (let i = 0; i < 8; i++) {
    const a = (i * Math.PI) / 4;
    const d = i % 2 ? r : R;
    g.lineTo(Math.cos(a) * d, Math.sin(a) * d);
  }
  g.closePath();
  g.fill();
}

/** Bursts: symbols of the current alphabet, not generic confetti — what bursts out
 *  belongs to the drawing. */
function drawBursts(): void {
  const g = S.g;
  const t = Math.max(S.cellW, S.cellH);
  for (const p of anim.parts) {
    const pr = (anim.t - p.t0) / p.life;
    if (pr >= 1) continue;
    g.save();
    g.globalAlpha = 1 - pr * pr;
    g.translate(p.x, p.y);
    g.rotate(p.rot);
    g.scale(1 - pr * 0.35, 1 - pr * 0.35);
    drawGlyph(g, -t / 2, -t / 2, t, t, p.code);
    g.restore();
  }
}

function drawMistake(): void {
  const g = S.g;
  if (!S.mistake) return;
  const p = (anim.t - S.mistake.t0) / 360;
  if (p >= 1) {
    S.mistake = null;
    return;
  }
  g.save();
  g.globalAlpha = (1 - p) * 0.95;
  g.strokeStyle = pal().mistake;
  g.lineWidth = 2.5 / S.viewport.z;
  g.strokeRect(S.mistake.c * S.cellW + 1, S.mistake.r * S.cellH + 1, S.cellW - 2, S.cellH - 2);
  g.fillStyle = pal().mistakeFill;
  g.fillRect(S.mistake.c * S.cellW + 1, S.mistake.r * S.cellH + 1, S.cellW - 2, S.cellH - 2);
  g.restore();
}

// ---------- progress ----------
export interface Progress {
  pct: number;
  done: number;
  todo: number;
}

/** Cells to fill and cells filled, per symbol. */
export interface CodeCount {
  todo: number;
  done: number;
}

/** Per-symbol tally, or null outside play. Same injection as setPct: the palette lives in
 *  ui.ts, which already depends on this module — a direct call would close an import cycle. */
let setCodeProgress: (p: readonly CodeCount[] | null) => void = () => {};
export function wireCodeProgress(f: (p: readonly CodeCount[] | null) => void): void {
  setCodeProgress = f;
}

export function updateProgress(): Progress | null {
  let todo = 0;
  let done = 0;
  // The per-symbol tally is built in this very loop rather than in a second pass: the grid
  // can hold 14400 cells and this runs on every painted zone.
  const per: CodeCount[] = Array.from({ length: NB_CODES }, () => ({ todo: 0, done: 0 }));
  for (let r = 0; r < S.gridH; r++)
    for (let c = 0; c < S.gridW; c++) {
      const m = S.target[r][c];
      // `m < per.length` : même raison que la garde de drawGlyph — un code hors table donnerait
      // `per[m]` indéfini et un TypeError à chaque zone peinte. Une case ignorée ne compte alors
      // ni dans le travail à faire ni dans le fait, ce qui est la seule réponse honnête pour une
      // case qu'aucune pastille ne peut peindre.
      if (m > 0 && m < per.length) {
        todo++;
        per[m].todo++;
        if (S.filled[r][c]) {
          done++;
          per[m].done++;
        }
      }
    }
  // 100 se mérite à la case près : sur une grille de plusieurs milliers de cases, l'arrondi
  // atteint 100 % une poignée de cases avant la fin — la barre se remplissait, et le dialogue
  // de fin tombait dessus, sur un dessin encore troué. Tout ce qui n'est pas terminé plafonne
  // donc à 99, et pct === 100 vaut exactement « il ne reste rien ».
  const pct = done >= todo ? 100 : Math.min(99, Math.round((done / todo) * 100));
  setCodeProgress(per);
  return { pct, done, todo };
}

export function applyZoom(nz: number, mx: number | null, my: number | null): void {
  // Every zoom that isn't a pinch lands here, and a player reaching for the zoom during a
  // milestone is asking for the sheet back: the harvest ends on the spot, and without restoring
  // the viewport it had pulled back from — the zoom being asked for IS the new framing, et il
  // s'applique à l'image que le joueur a sous les yeux, pas à celle qu'il avait avant le recul.
  animFlareEnd('keep');
  const z = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, nz));
  const before = S.viewport.z;
  // with no anchor point, we zoom on the screen's center — not the grid's, which may
  // be off-screen
  const ax = mx ?? S.availW / 2;
  const ay = my ?? S.availH / 2;
  S.viewport.x = ax - (ax - S.viewport.x) * (z / before);
  S.viewport.y = ay - (ay - S.viewport.y) * (z / before);
  S.viewport.z = z;
  invalidateLayers(); // the layers are rasterized at the current scale
  // Every zoom that isn't a pinch comes through here — wheel, slider, buttons — and a zoom is
  // the gesture the counts matter most for: it is how you come back to the overview.
  animPoke();
  render();
}
